import React, { useState, useEffect, useMemo } from "react";
import axios from "axios";
import { API, useAuth } from "@/App";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { CompanyAutocomplete } from "@/components/CompanyAutocomplete";
import { toast } from "sonner";
import {
  Plus,
  Truck,
  Search,
  Phone,
  MapPin,
  Package,
  Edit,
  Trash2,
  Filter,
  Upload,
  FileText,
  Loader2,
  Euro,
  AlertCircle,
  Check,
  X,
  AlertTriangle,
  CheckSquare,
  Calendar,
  Route,
  Gift,
  UserCheck,
} from "lucide-react";

const STATUT_OPTIONS = [
  { value: "en_attente_validation", label: "À valider", color: "bg-amber-100 text-amber-700" },
  { value: "en_attente", label: "En attente", color: "bg-yellow-100 text-yellow-700" },
  { value: "en_cours", label: "En cours", color: "bg-blue-100 text-blue-700" },
  { value: "livree", label: "Livree", color: "bg-green-100 text-green-700" },
  { value: "reportee", label: "Reportee", color: "bg-orange-100 text-orange-700" },
  { value: "annulee", label: "Annulee", color: "bg-red-100 text-red-700" },
];

const getStatutBadge = (statut) => {
  const option = STATUT_OPTIONS.find((o) => o.value === statut);
  return option ? (
    <Badge className={`${option.color} border-0`}>{option.label}</Badge>
  ) : (
    <Badge variant="outline">{statut}</Badge>
  );
};

// Normalize string for comparison
const normalize = (str) => (str || "").toLowerCase().trim().replace(/\s+/g, " ");

// Détection d'email (optionnelle) dans le texte importé
const EMAIL_RE = /[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}/;
const extractEmail = (s) => { const m = (s || "").match(EMAIL_RE); return m ? m[0].toLowerCase() : ""; };
const stripEmail = (s) => (s || "").replace(EMAIL_RE, "").replace(/\s{2,}/g, " ").replace(/\s*[|;,]\s*$/,"").trim();

// ======= UNIVERSAL ADDRESS PARSER =======
const KNOWN_COUNTRIES = ["belgique", "belgium", "france", "pays-bas", "nederland", "netherlands", "luxembourg", "allemagne", "germany", "deutschland", "suisse", "switzerland", "espagne", "spain", "italie", "italy", "portugal", "maroc", "morocco"];

const parseFullAddress = (fullAddress) => {
  let adresse = fullAddress.trim(), codePostal = "", ville = "", pays = "";

  // Strategy 1: "Street, CP City, Country" (e.g. "46 Rue X, 6200 Châtelet, Belgique")
  const m1 = fullAddress.match(/^(.+?),\s*(\d{4,5})\s+([^,]+?)(?:,\s*(.+))?$/);
  if (m1) {
    adresse = m1[1].trim();
    codePostal = m1[2];
    ville = m1[3].trim();
    pays = (m1[4] || "").trim();
    if (!pays) pays = codePostal.length === 5 ? "France" : "Belgique";
    return { adresse, codePostal, ville, pays };
  }

  // Strategy 2: "Street, City, Country" without postal code (e.g. "Wittenonnestraat 33, Oostende, Belgique")
  const parts = fullAddress.split(",").map(p => p.trim());
  if (parts.length >= 3) {
    const lastPart = parts[parts.length - 1].toLowerCase();
    if (KNOWN_COUNTRIES.includes(lastPart)) {
      pays = parts[parts.length - 1];
      ville = parts[parts.length - 2];
      adresse = parts.slice(0, parts.length - 2).join(", ");
      // Check if ville starts with a postal code
      const cpInVille = ville.match(/^(\d{4,5})\s+(.+)$/);
      if (cpInVille) { codePostal = cpInVille[1]; ville = cpInVille[2]; }
      return { adresse, codePostal, ville, pays };
    }
  }

  // Strategy 3: "Street, CP City" (2 parts, no country)
  if (parts.length === 2) {
    const cpCity = parts[1].match(/^(\d{4,5})\s+(.+)$/);
    if (cpCity) {
      adresse = parts[0];
      codePostal = cpCity[1];
      ville = cpCity[2];
      pays = codePostal.length === 5 ? "France" : "Belgique";
      return { adresse, codePostal, ville, pays };
    }
    // "Street, City" (no CP)
    adresse = parts[0];
    ville = parts[1];
    return { adresse, codePostal: "", ville, pays: "Belgique" };
  }

  // Fallback: just keep the whole string as address
  return { adresse, codePostal: "", ville: "", pays: "Belgique" };
};

// ======= UNIVERSAL PARSER =======
// Detects format at document level, supports: Pipe, Tab/Spreadsheet, Space/Text, Semicolon, Comma

const COLUMN_ALIASES = {
  nom: "nom", name: "nom", client: "nom", "client_nom": "nom", prenom: "nom",
  telephone: "telephone", tel: "telephone", phone: "telephone", gsm: "telephone", mobile: "telephone", "client_telephone": "telephone", numero: "telephone", num: "telephone",
  adresse: "adresse", address: "adresse", rue: "adresse", street: "adresse",
  cp: "code_postal", "code postal": "code_postal", "code_postal": "code_postal", postal: "code_postal", zip: "code_postal",
  ville: "ville", city: "ville", commune: "ville", localite: "ville",
  quantite: "quantite", qty: "quantite", qte: "quantite", nb: "quantite", nombre: "quantite", quantit: "quantite",
  prix: "prix", price: "prix", total: "prix", montant: "prix",
  details: "details", commande: "details", produit: "details", article: "details", description: "details", "details_commande": "details", couleur: "details", detail: "details", couleurs: "details", "detail/couleurs": "details", "detailcouleurs": "details",
  notes: "notes", remarque: "notes", commentaire: "notes", note: "notes",
  pays: "pays", country: "pays",
  statut: "statut", status: "statut", etat: "statut",
};

const detectColumnMap = (headerCells) => {
  const map = {};
  // Column 0 is ALWAYS the client name — regardless of what the header says
  map.nom = 0;
  headerCells.forEach((raw, idx) => {
    if (idx === 0) return; // Skip col 0, always nom
    const key = raw.toLowerCase().replace(/[^a-zà-öø-ÿ_]/g, "").trim();
    if (COLUMN_ALIASES[key] && !map[COLUMN_ALIASES[key]]) map[COLUMN_ALIASES[key]] = idx;
    // Try partial match
    if (!Object.values(map).includes(idx)) {
      for (const [alias, field] of Object.entries(COLUMN_ALIASES)) {
        if (key.includes(alias) && !map[field]) { map[field] = idx; break; }
      }
    }
  });
  return Object.keys(map).length >= 2 ? map : null;
};

const isHeaderRow = (cells) => {
  const lowerCells = cells.map(c => c.toLowerCase().replace(/[^a-zà-öø-ÿ_\s]/g, "").trim());
  const headerWords = ["nom", "name", "client", "telephone", "tel", "phone", "adresse", "address", "rue", "ville", "city", "prix", "price", "quantite", "qty", "details", "produit", "statut", "cp"];
  let matches = 0;
  for (const cell of lowerCells) {
    if (headerWords.some(hw => cell.includes(hw))) matches++;
  }
  return matches >= 2;
};

const isSeparatorRow = (line) => /^[\s|+\-:=]+$/.test(line);

// Detect "totals" rows: empty name + only numbers (e.g. "\t\t\t \t705")
const isTotalsRow = (cells) => {
  const hasNoName = !cells[0] || !cells[0].trim();
  const allNonNameAreNumericOrEmpty = cells.slice(1).every(c => !c.trim() || /^[\d.,\s€$]+$/.test(c.trim()));
  return hasNoName && allNonNameAreNumericOrEmpty;
};

const cellToResult = (cells, colMap) => {
  const get = (field) => (colMap[field] !== undefined && cells[colMap[field]]) ? cells[colMap[field]].trim() : "";
  const client_email = (cells || []).map(c => extractEmail(c)).find(Boolean) || "";
  const rawName = stripEmail(get("nom"));
  const rawPhone = get("telephone").replace(/\s/g, "");
  const rawAddress = stripEmail(get("adresse"));
  const rawCp = get("code_postal");
  const rawVille = stripEmail(get("ville"));
  const rawDetails = stripEmail(get("details"));
  const rawNotes = get("notes");
  const rawStatut = get("statut");
  const rawPays = get("pays");
  const rawQty = get("quantite");
  const rawPrix = get("prix");

  if (!rawName && !rawPhone && !rawAddress) return null;

  // If no separate CP/ville columns, try parsing from address
  let adresse = rawAddress, codePostal = rawCp, ville = rawVille, pays = rawPays || "Belgique";
  if (!codePostal && !ville && rawAddress) {
    const parsed = parseFullAddress(rawAddress);
    adresse = parsed.adresse;
    codePostal = parsed.codePostal;
    ville = parsed.ville;
    pays = parsed.pays || pays;
  }

  let phone = rawPhone;
  if (/^\d{8,9}$/.test(phone)) phone = "0" + phone;

  return {
    client_nom: rawName,
    client_telephone: phone,
    client_email,
    adresse, code_postal: codePostal, ville,
    quantite: parseInt(rawQty) || 1,
    prix: rawPrix ? parseFloat(rawPrix.replace(/[€$\s]/g, "").replace(",", ".")) || null : null,
    details_commande: rawDetails,
    pays,
    notes: rawStatut ? `Statut: ${rawStatut}` : rawNotes,
  };
};

const autoDetectColumns = (cells) => {
  const map = {};
  const used = new Set();

  // RULE 1: Column 0 is ALWAYS the client name
  map.nom = 0;
  used.add(0);

  // RULE 1b: reserve the EMAIL column so it isn't mistaken for the address
  const emailIdx = cells.findIndex((c, i) => i > 0 && EMAIL_RE.test(c || ""));
  if (emailIdx > 0) {
    map.email = emailIdx;
    used.add(emailIdx);
  }

  // Score each cell to determine its type (starting from column 1)
  const scores = cells.map((cell, idx) => {
    const c = (cell || "").trim();
    const digits = c.replace(/\D/g, "");
    
    // Detect if it's a product detail (last column typically)
    const hasProductKeyword = /\bmilan\b|\bking\b|\blouis\b|\bchaise\b/i.test(c);
    const hasQuantityPattern = /[xX]\s?\d+|\d+\s*[xX]/.test(c);
    const hasColorKeyword = /\b(noir|blanc|gris|bleu|vert|rose|rouge|orange|black|white|grey|gray|green|blue|pink|beige|marron|brown|yellow|jaune|velvet|velours)\b/i.test(c);

    // Phone: 8-12 digit number — NEVER a price (prices have < 8 digits)
    const isPhoneCandidate = idx > 0 && /^0?\d{8,12}$/.test(c.replace(/[\s./-]/g, ""));

    // Simple description: just a color name, "6 black", "3x noir", etc.
    const isSimpleDescription = idx > 0 && !isPhoneCandidate && (
      /^\d+\s+(noir|blanc|gris|bleu|vert|rose|rouge|orange|black|white|grey|gray|green|blue|pink|beige|marron|brown|yellow|jaune|velvet)$/i.test(c.trim()) ||
      /^(noir|blanc|gris|bleu|vert|rose|rouge|orange|black|white|grey|gray|green|blue|pink|beige|marron|brown|yellow|jaune|velvet|velours)(\s+\d+)?$/i.test(c.trim())
    );
    const isProductDetails = idx > 0 && !isPhoneCandidate && (
      (hasProductKeyword && (hasQuantityPattern || hasColorKeyword)) ||
      (hasQuantityPattern && hasColorKeyword) ||
      isSimpleDescription
    );

    // Price: has € sign OR reasonable price value (< 8 digits) — never phone-like
    const isPriceCandidate = idx > 0 && !isPhoneCandidate && (
      /[€$]/.test(c) ||
      (/^\d+([.,]\d+)?$/.test(c) && parseFloat(c.replace(",",".")) > 50 && digits.length < 8)
    );

    return {
      idx,
      isCP: /^\d{4,5}$/.test(c),
      isPhone: isPhoneCandidate,
      isQty: idx > 0 && /^\d{1,3}$/.test(c) && parseInt(c) <= 200 && !isPhoneCandidate,
      isPrix: isPriceCandidate,
      isAddress: idx > 0 && (c.length > 10 && /\d/.test(c) && /[a-zA-ZÀ-ÖØ-öø-ÿ]/.test(c)),
      isDetails: isProductDetails,
      isEmpty: !c,
      raw: c,
    };
  });

  // 1. Find CP (4-5 digit postal code)
  for (const s of scores) {
    if (s.isCP && !used.has(s.idx)) {
      map.code_postal = s.idx;
      used.add(s.idx);
      break;
    }
  }

  // 2. Find ville: right after CP if available
  if (map.code_postal !== undefined) {
    const nextIdx = map.code_postal + 1;
    if (nextIdx < cells.length && !used.has(nextIdx)) {
      const nextCell = cells[nextIdx];
      if (nextCell && !/^\d+$/.test(nextCell.trim())) {
        map.ville = nextIdx;
        used.add(nextIdx);
      }
    }
  }

  // 3. Find details (LAST column with product info)
  for (let i = cells.length - 1; i > 0; i--) {
    if (scores[i].isDetails && !used.has(i)) {
      map.details = i;
      used.add(i);
      break;
    }
  }

  // 4. Find phone BEFORE prix (phone detection must run first)
  for (const s of scores) {
    if (s.isPhone && !used.has(s.idx)) {
      map.telephone = s.idx;
      used.add(s.idx);
      break;
    }
  }

  // 5. Find prix (after phone is claimed)
  for (const s of scores) {
    if (s.isPrix && !used.has(s.idx)) {
      map.prix = s.idx;
      used.add(s.idx);
      break;
    }
  }

  // 6. Find quantity (1-3 digit small number)
  for (const s of scores) {
    if (s.isQty && !used.has(s.idx)) {
      map.quantite = s.idx;
      used.add(s.idx);
      break;
    }
  }

  // 7. Find address: column 1 is typically the address (right after name)
  if (!used.has(1) && cells[1] && cells[1].length > 5) {
    map.adresse = 1;
    used.add(1);
  } else {
    for (const s of scores) {
      if (s.isAddress && !used.has(s.idx)) {
        map.adresse = s.idx;
        used.add(s.idx);
        break;
      }
    }
  }

  // Fallback: if detection failed completely
  if (Object.keys(map).length < 3) {
    return { nom: 0, adresse: 1, telephone: 2 };
  }

  return map;
};

const parseStructuredLines = (lines, separator) => {
  const results = [];
  let colMap = null;
  const errors = [];

  // Pre-detect format for tab- or pipe-separated data with 7-8 columns
  // Format A: Nom | Adresse | CP | Ville | Qte | Tel | Prix | Details
  // Format B: Nom | Tel | Adresse | CP | Ville | Qte | Prix | Details
  if ((separator === "\t" || separator === "|") && lines.length > 0) {
    // Find first non-empty line
    for (const line of lines) {
      if (!line.trim() || isSeparatorRow(line)) continue;
      let cells;
      if (separator === "|") {
        cells = line.split("|").map(c => c.trim());
        while (cells.length > 0 && cells[0] === "") cells.shift();
        while (cells.length > 0 && cells[cells.length - 1] === "") cells.pop();
      } else {
        cells = line.split("\t").map(c => c.trim());
      }

      // 6-column format: Nom | Tel | FullAddress | Qty | Prix | Details
      if (cells.length === 6) {
        const col1LooksLikePhone = /^0?\d{8,12}$/.test(cells[1].replace(/[\s./-]/g, ""));
        const col2LooksLikeFullAddress = cells[2].length > 10 && /\d/.test(cells[2]) && /[a-zA-ZÀ-ÖØ-öø-ÿ]/.test(cells[2]);
        const col3LooksLikeQty = /^\d{1,3}$/.test(cells[3]) && parseInt(cells[3]) <= 200;
        if (col1LooksLikePhone && col2LooksLikeFullAddress && col3LooksLikeQty) {
          colMap = { nom: 0, telephone: 1, adresse: 2, quantite: 3, prix: 4, details: 5 };
          break;
        }
      }

      // 5-column format: Nom | Tel | FullAddress | Qty | Prix
      if (cells.length === 5) {
        const col1LooksLikePhone = /^0?\d{8,12}$/.test(cells[1].replace(/[\s./-]/g, ""));
        const col2LooksLikeFullAddress = cells[2].length > 10 && /\d/.test(cells[2]) && /[a-zA-ZÀ-ÖØ-öø-ÿ]/.test(cells[2]);
        const col3LooksLikeQty = /^\d{1,3}$/.test(cells[3]) && parseInt(cells[3]) <= 200;
        if (col1LooksLikePhone && col2LooksLikeFullAddress && col3LooksLikeQty) {
          colMap = { nom: 0, telephone: 1, adresse: 2, quantite: 3, prix: 4 };
          break;
        }
      }

      if (cells.length >= 7) {
        const lastColIdx = cells.length - 1;
        const lastCell = cells[lastColIdx] || "";
        // If last column looks like product details
        const isLastColProducts = /[xX]\s?\d+|\d+\s*[xX]|\bmilan\b|\bking\b|\blouis\b/i.test(lastCell);

        // 8-col format with telephone in col 1: Nom | Tel | Adresse | CP | Ville | Qte | Prix | Details
        if (cells.length >= 8) {
          const col1LooksLikePhone = /^[+]?\d[\d\s./-]{7,}$/.test(cells[1]);
          const col3LooksLikeCP = /^\d{4,5}$/.test(cells[3]);
          if (isLastColProducts && col1LooksLikePhone && col3LooksLikeCP) {
            colMap = {
              nom: 0, telephone: 1, adresse: 2, code_postal: 3, ville: 4,
              quantite: 5, prix: 6, details: lastColIdx,
            };
            break;
          }
        }

        // Also check if column 2 looks like a postal code
        const col2LooksLikeCP = /^\d{4,5}$/.test(cells[2]);

        if (isLastColProducts && col2LooksLikeCP) {
          colMap = {
            nom: 0,
            adresse: 1,
            code_postal: 2,
            ville: 3,
            quantite: 4,
            telephone: 5,
            prix: 6,
            details: lastColIdx
          };
          break;
        }
      }
      break; // Only check first data line
    }
  }

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    if (!line.trim() || isSeparatorRow(line)) continue;

    let cells;
    if (separator === "|") {
      cells = line.split("|").map(c => c.trim());
      // Remove empty leading/trailing cells from pipe format
      while (cells.length > 0 && cells[0] === "") cells.shift();
      while (cells.length > 0 && cells[cells.length - 1] === "") cells.pop();
    } else {
      cells = line.split(separator).map(c => c.trim());
    }

    if (cells.length < 2) continue;

    // Try header detection on first data row (only if no colMap yet)
    if (!colMap && isHeaderRow(cells)) {
      const headerMap = detectColumnMap(cells);
      // Only use header map if it has enough meaningful fields (adresse or telephone detected)
      if (headerMap && (headerMap.adresse !== undefined || headerMap.telephone !== undefined)) {
        colMap = headerMap;
      }
      // Otherwise skip this header row and use autoDetect on next row
      continue;
    }

    // If we still have no colMap, use auto-detect
    if (!colMap) {
      // Smart auto-detect: analyze cell content across multiple rows for better accuracy
      const sampleRows = [];
      for (let j = i; j < Math.min(i + 5, lines.length); j++) {
        const sampleLine = lines[j];
        if (!sampleLine || !sampleLine.trim() || isSeparatorRow(sampleLine)) continue;
        const sampleCells = separator === "|"
          ? sampleLine.split("|").map(c => c.trim()).filter((c, idx, arr) => idx > 0 || c)
          : sampleLine.split(separator).map(c => c.trim());
        if (sampleCells.length >= 3) sampleRows.push(sampleCells);
      }
      // Use the row with most non-empty cells for detection
      const bestRow = sampleRows.sort((a, b) => b.filter(c => c).length - a.filter(c => c).length)[0] || cells;
      colMap = autoDetectColumns(bestRow);
    }

    // Handle special "Total: N" and "Statut: X" cells in pipe format
    const hasTotal = cells.some(c => /^Total\s*:/i.test(c));
    const hasStatut = cells.some(c => /^Statut\s*:/i.test(c));
    if (hasTotal || hasStatut) {
      const totalCell = cells.find(c => /^Total\s*:/i.test(c));
      const qtyMatch = totalCell ? totalCell.match(/Total\s*:\s*(\d+)/i) : null;
      const statutCell = cells.find(c => /^Statut\s*:/i.test(c));
      const name = cells[0] || "";
      const phone = (cells[1] || "").replace(/\s/g, "");
      const fullAddr = cells[2] || "";
      const details = cells[3] || "";
      const { adresse, codePostal, ville, pays: p } = parseFullAddress(fullAddr);
      results.push({
        client_nom: name, client_telephone: phone,
        adresse, code_postal: codePostal, ville,
        quantite: qtyMatch ? parseInt(qtyMatch[1]) : 1, prix: null,
        details_commande: details,
        pays: p || "Belgique",
        notes: statutCell ? statutCell.replace(/^Statut\s*:\s*/i, "").trim() : "",
      });
      continue;
    }

    const result = cellToResult(cells, colMap);
    if (result) {
      results.push(result);
    } else {
      if (!isTotalsRow(cells)) errors.push(i + 1);
    }
  }
  return { results, errors };
};

const parseSpaceLines = (lines) => {
  const results = [];
  const errors = [];

  for (let i = 0; i < lines.length; i++) {
    const rawLine = lines[i].trim();
    if (!rawLine || isSeparatorRow(rawLine)) continue;
    const client_email = extractEmail(rawLine);
    const line = stripEmail(rawLine);

    // Extract phone number (international formats)
    const phoneMatch = line.match(/\b(\+?\d{10,13}|0\d{8,9}|00\d{10,12})\b/);
    if (!phoneMatch) {
      // Try with spaces in phone: +32 488 23 92 17
      const spacedPhone = line.match(/(\+\d{2}\s?\d{3}\s?\d{2}\s?\d{2}\s?\d{2})/);
      if (spacedPhone) {
        const phone = spacedPhone[1].replace(/\s/g, "");
        const phoneIdx = line.indexOf(spacedPhone[1]);
        const name = line.substring(0, phoneIdx).replace(/[,\s]+$/, "").trim();
        const afterPhone = line.substring(phoneIdx + spacedPhone[1].length).trim();
        const parsed = parseAfterPhone(name, phone, afterPhone);
        if (parsed) { results.push({ ...parsed, client_email }); continue; }
      }
      errors.push(i + 1);
      continue;
    }

    const phone = phoneMatch[1];
    const phoneIdx = line.indexOf(phone);
    const name = line.substring(0, phoneIdx).replace(/[,\s]+$/, "").trim();
    const afterPhone = line.substring(phoneIdx + phone.length).trim();
    const parsed = parseAfterPhone(name, phone, afterPhone);
    if (parsed) { results.push({ ...parsed, client_email }); continue; }

    // Fallback: just store as name + phone
    results.push({
      client_nom: name || line, client_telephone: phone, client_email,
      adresse: afterPhone, code_postal: "", ville: "",
      quantite: 1, prix: null, details_commande: "",
      pays: "Belgique", notes: "",
    });
  }
  return { results, errors };
};

const parseAfterPhone = (name, phone, afterPhone) => {
  if (!afterPhone) return null;

  // Remove leading comma/space
  let rest = afterPhone.replace(/^[,\s]+/, "").trim();

  // Try: "Street, CP City Qty Price Details"
  const commaIdx = rest.indexOf(",");
  if (commaIdx === -1) {
    // No comma: "Street CP City Qty Price Details" — try address as whole
    const { adresse, codePostal, ville, pays } = parseFullAddress(rest);
    return {
      client_nom: name, client_telephone: phone,
      adresse, code_postal: codePostal, ville,
      quantite: 1, prix: null, details_commande: "",
      pays: pays || "Belgique", notes: "",
    };
  }

  const street = rest.substring(0, commaIdx).trim();
  const afterComma = rest.substring(commaIdx + 1).trim();

  // Try: "CP City Qty Price Details" or "CP City Details"
  const cpMatch = afterComma.match(/^(\d{4,5})\s+(.*)$/);
  if (cpMatch) {
    const cp = cpMatch[1];
    const remainder = cpMatch[2];
    const detectedPays = cp.length === 5 ? "France" : "Belgique";

    // Try: "City Qty Price Details"
    const cityQtyPrice = remainder.match(
      /^([A-Za-zÀ-ÖØ-öø-ÿ][A-Za-zÀ-ÖØ-öø-ÿ\s\-'.]*?)\s+(\d{1,3})\s+(\d+(?:[.,]\d+)?)\s*(.*)$/
    );
    if (cityQtyPrice) {
      return {
        client_nom: name, client_telephone: phone,
        adresse: street, code_postal: cp,
        ville: cityQtyPrice[1].trim(),
        quantite: parseInt(cityQtyPrice[2]),
        prix: parseFloat(cityQtyPrice[3].replace(",", ".")),
        details_commande: cityQtyPrice[4].trim(),
        pays: detectedPays, notes: "",
      };
    }

    // Try: "City Qty Details" (no price)
    const cityQty = remainder.match(
      /^([A-Za-zÀ-ÖØ-öø-ÿ][A-Za-zÀ-ÖØ-öø-ÿ\s\-'.]*?)\s+(\d{1,3})\s+(.+)$/
    );
    if (cityQty) {
      return {
        client_nom: name, client_telephone: phone,
        adresse: street, code_postal: cp,
        ville: cityQty[1].trim(),
        quantite: parseInt(cityQty[2]),
        prix: null,
        details_commande: cityQty[3].trim(),
        pays: detectedPays, notes: "",
      };
    }

    // Just "City" or "City, Country"
    const cityCountry = remainder.split(",").map(p => p.trim());
    return {
      client_nom: name, client_telephone: phone,
      adresse: street, code_postal: cp,
      ville: cityCountry[0],
      quantite: 1, prix: null, details_commande: "",
      pays: cityCountry[1] || detectedPays, notes: "",
    };
  }

  // No CP: parse remaining as address continuation
  const { adresse, codePostal, ville, pays } = parseFullAddress(`${street}, ${afterComma}`);
  return {
    client_nom: name, client_telephone: phone,
    adresse: adresse || street, code_postal: codePostal, ville,
    quantite: 1, prix: null, details_commande: "",
    pays: pays || "Belgique", notes: "",
  };
};

// Detect dominant format of the pasted text
const detectFormat = (text) => {
  const lines = text.trim().split("\n").filter(l => l.trim() && !isSeparatorRow(l));
  if (lines.length === 0) return { format: "unknown", lines: [] };

  let pipeCount = 0, tabCount = 0, semiCount = 0;
  for (const line of lines) {
    if (line.includes("|")) pipeCount++;
    if (line.includes("\t")) tabCount++;
    if (line.includes(";")) semiCount++;
  }

  const total = lines.length;
  // A format is dominant if >50% of lines use it
  if (tabCount > total * 0.5) return { format: "tab", lines };
  if (pipeCount > total * 0.5) return { format: "pipe", lines };
  if (semiCount > total * 0.5) return { format: "semicolon", lines };
  return { format: "space", lines };
};

const FORMAT_LABELS = {
  tab: "Tableur (Tab)",
  pipe: "Pipe (|)",
  semicolon: "Point-virgule (;)",
  space: "Texte libre",
  unknown: "Inconnu",
};

const parseRawText = (text) => {
  const { format, lines } = detectFormat(text);
  if (lines.length === 0) return { results: [], format: "unknown", errors: [] };

  let parsed;
  if (format === "tab") {
    parsed = parseStructuredLines(lines, "\t");
  } else if (format === "pipe") {
    parsed = parseStructuredLines(lines, "|");
  } else if (format === "semicolon") {
    parsed = parseStructuredLines(lines, ";");
  } else {
    parsed = parseSpaceLines(lines);
  }

  // Fallback for unparsed lines: try comma-split
  if (parsed.results.length === 0 && lines.length > 0) {
    const fallbackResults = [];
    for (const line of lines) {
      const parts = line.split(",").map(p => p.trim());
      if (parts.length >= 2) {
        const last = parts[parts.length - 1];
        const cpMatch = last.match(/^(\d{4})\s+(.+)$/);
        fallbackResults.push({
          client_nom: parts[0], client_telephone: "",
          adresse: parts.slice(1, cpMatch ? -1 : undefined).join(", "),
          code_postal: cpMatch ? cpMatch[1] : "",
          ville: cpMatch ? cpMatch[2] : parts.length > 2 ? parts[2] : "",
          quantite: 1, prix: null, details_commande: "",
          pays: "Belgique", notes: "",
        });
      }
    }
    return { results: fallbackResults, format: "comma", errors: [] };
  }

  return { results: parsed.results, format, errors: parsed.errors || [] };
};

const Livraisons = () => {
  const { token } = useAuth();
  const [livraisons, setLivraisons] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [rawDialogOpen, setRawDialogOpen] = useState(false);
  const [editingLivraison, setEditingLivraison] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterStatut, setFilterStatut] = useState("all");
  const [filterVille, setFilterVille] = useState("all");
  const [filterDateStart, setFilterDateStart] = useState("");
  const [filterDateEnd, setFilterDateEnd] = useState("");
  const [filterTournee, setFilterTournee] = useState("all");
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);

  // Raw import state
  const [rawText, setRawText] = useState("");
  const [parsedItems, setParsedItems] = useState([]);
  const [showPreview, setShowPreview] = useState(false);
  const [importing, setImporting] = useState(false);
  const [selectedPreview, setSelectedPreview] = useState(new Set());
  const [importDate, setImportDate] = useState(new Date().toISOString().split("T")[0]);
  const [importHistorique, setImportHistorique] = useState(false);
  const [importDeduireStock, setImportDeduireStock] = useState(false);
  const [importFraisInclus, setImportFraisInclus] = useState(false);
  const [produits, setProduits] = useState([]);
  const [detectedFormat, setDetectedFormat] = useState("unknown");
  const [parseErrors, setParseErrors] = useState([]);

  // Post-import: create tournée
  const [postImportIds, setPostImportIds] = useState([]);
  const [showPostImport, setShowPostImport] = useState(false);
  const [creatingTournee, setCreatingTournee] = useState(false);

  // Main table selection
  const [selectedRows, setSelectedRows] = useState(new Set());

  // Ajout à une tournée existante
  const [addTourneeDialogOpen, setAddTourneeDialogOpen] = useState(false);
  const [addTourneeLivraison, setAddTourneeLivraison] = useState(null);
  const [tourneesActives, setTourneesActives] = useState([]);
  const [tourneeMode, setTourneeMode] = useState("existante");
  const [newTourneeNom, setNewTourneeNom] = useState("");
  const [newTourneeDate, setNewTourneeDate] = useState("");
  const [selectedTourneeId, setSelectedTourneeId] = useState("");
  const [addingToTournee, setAddingToTournee] = useState(false);

  // Status change with notes
  const [statusDialogOpen, setStatusDialogOpen] = useState(false);
  const [statusChangeTarget, setStatusChangeTarget] = useState(null);
  const [statusNote, setStatusNote] = useState("");
  const [newStatut, setNewStatut] = useState("");

  const [formData, setFormData] = useState({
    client_nom: "",
    client_telephone: "",
    client_email: "",
    adresse: "",
    ville: "",
    code_postal: "",
    pays: "Belgique",
    details_commande: "",
    quantite: 1,
    prix: "",
    frais_livraison: 25,
    notes: "",
    date_livraison_prevue: "",
    // Options pour livraisons historiques
    est_historique: false,
    deduire_stock: true,
    // Canal client
    canal: "B2C",
    // Parrainage
    parrain_id: "",
    // ── LOT 1 ──
    mode_commande: "livraison",
    client_societe: "",
    client_tva: "",
    remarques: "",
    // ── LOT 2 ──
    montant_total: "",
    acompte: "",
    acompte_mode: "liquide",
    // Produits liés (commandes mixtes)
    produits_lies: [],
  });

  const [parrainsList, setParrainsList] = useState([]);
  const [parrainSearch, setParrainSearch] = useState("");

  // Client existant : recherche + résultats
  const [clientSearchQuery, setClientSearchQuery] = useState("");
  const [clientSearchResults, setClientSearchResults] = useState([]);
  const [clientSearchOpen, setClientSearchOpen] = useState(false);
  const [clientSearchLoading, setClientSearchLoading] = useState(false);

  const headers = { Authorization: `Bearer ${token}` };

  // Debounced client search
  useEffect(() => {
    if (!clientSearchQuery || clientSearchQuery.trim().length < 2) {
      setClientSearchResults([]);
      return;
    }
    const t = setTimeout(async () => {
      try {
        setClientSearchLoading(true);
        const res = await axios.get(`${API}/client-search`, {
          headers,
          params: { q: clientSearchQuery.trim() },
        });
        setClientSearchResults(res.data || []);
        setClientSearchOpen(true);
      } catch {
        setClientSearchResults([]);
      } finally {
        setClientSearchLoading(false);
      }
    }, 300);
    return () => clearTimeout(t);
    // eslint-disable-next-line
  }, [clientSearchQuery]);

  const selectExistingClient = (c) => {
    setFormData((prev) => ({
      ...prev,
      client_nom: c.nom || "",
      client_telephone: c.telephone || "",
      client_email: c.email || "",
      adresse: c.adresse || "",
      code_postal: c.code_postal || "",
      ville: c.ville || "",
      pays: c.pays || "Belgique",
      client_societe: c.societe || "",
      client_tva: c.tva || "",
      remarques: c.remarques || "",
    }));
    setClientSearchOpen(false);
    setClientSearchQuery("");
    toast.success(`Client « ${c.nom} » chargé (${c.nombre_commandes || 0} commande${(c.nombre_commandes || 0) > 1 ? "s" : ""})`);
  };

  const fetchData = async () => {
    try {
      const [livraisonsRes, statsRes, produitsRes] = await Promise.all([
        axios.get(`${API}/livraisons`, { headers }),
        axios.get(`${API}/livraisons-stats`, { headers }),
        axios.get(`${API}/produits`, { headers }),
      ]);
      setLivraisons(livraisonsRes.data);
      setStats(statsRes.data);
      setProduits(produitsRes.data);
    } catch (error) {
      toast.error("Erreur lors du chargement des livraisons");
    } finally {
      setLoading(false);
    }
  };

  // Load potential parrains (clients with parrainage_code)
  useEffect(() => {
    axios.get(`${API}/communications/client-contacts`, { headers })
      .then(r => setParrainsList(r.data || []))
      .catch(() => {});
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    fetchData();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ---- Duplicate detection ----
  const detectDuplicates = (items) => {
    const flags = items.map(() => ({ inBatch: false, inDb: false }));

    // Check within batch
    for (let i = 0; i < items.length; i++) {
      for (let j = i + 1; j < items.length; j++) {
        if (
          normalize(items[i].client_nom) === normalize(items[j].client_nom) &&
          normalize(items[i].adresse) === normalize(items[j].adresse)
        ) {
          flags[i].inBatch = true;
          flags[j].inBatch = true;
        }
      }
    }

    // Check against existing livraisons in DB
    for (let i = 0; i < items.length; i++) {
      const exists = livraisons.some(
        (l) =>
          normalize(l.client_nom) === normalize(items[i].client_nom) &&
          normalize(l.adresse) === normalize(items[i].adresse)
      );
      if (exists) flags[i].inDb = true;
    }

    return flags;
  };

  const duplicateFlags = useMemo(
    () => detectDuplicates(parsedItems),
    [parsedItems, livraisons] // eslint-disable-line react-hooks/exhaustive-deps
  );

  const duplicateCount = duplicateFlags.filter((f) => f.inBatch || f.inDb).length;

  // ---- Auto-match product from details text ----
  const COLOR_ALIASES = {
    "black": "noir", "noire": "noir", "white": "blanc", "blue": "bleu",
    "grey": "gris", "gray": "gris", "green": "vert", "red": "rouge",
    "yellow": "jaune", "pink": "rose", "rose": "rose",
    "sand": "beige", "beige": "beige", "creme": "beige", "crème": "beige",
    "transparent": "transparent", "clair": "transparent", "translucide": "transparent",
    "ghost": "ghost",
  };

  const matchSingleProduct = (text) => {
    if (!text || !produits.length) return null;
    const q = text.toLowerCase().replace(/x?\d+/g, "").trim();
    if (!q) return null;
    const words = q.replace(/[|,()]/g, " ").split(/\s+/).filter(Boolean);

    let bestMatch = null;
    let bestScore = 0;
    let bestHasColor = false;
    let modelOnlyCount = 0; // nb de produits qui matchent le modèle SANS couleur identifiée

    for (const p of produits) {
      const model = (p.modele || "").toLowerCase();
      const color = (p.couleur || "").toLowerCase();
      const sku = (p.sku || "").toLowerCase();
      let modelHit = false, colorHit = false, skuHit = false;
      for (const w of words) {
        const alias = COLOR_ALIASES[w] || w;
        if (model && (model.includes(w) || w.includes(model))) modelHit = true;
        if (color && (color.includes(w) || color.includes(alias))) colorHit = true;
        if (sku && w.includes("-") && sku.includes(w) && w.length >= 3) skuHit = true;
      }
      const score = (skuHit ? 200 : 0) + (colorHit ? 100 : 0) + (modelHit ? 30 : 0);
      if (modelHit && !colorHit && !skuHit) modelOnlyCount++;
      if (score > bestScore) {
        bestScore = score;
        bestMatch = p;
        bestHasColor = colorHit || skuHit;
      }
    }

    if (!bestMatch || bestScore < 30) return null;
    // Si on a identifié une couleur (ou un SKU) → match fiable.
    if (bestHasColor) return bestMatch;
    // Sinon (modèle seul) : ne JAMAIS deviner une couleur au hasard si plusieurs produits du même modèle existent.
    if (modelOnlyCount <= 1) return bestMatch;
    return null;
  };

  // Parse "3 Milan Black + 3 Milan Bleu Gris" or "Milan Noir x2 | Milan Blanc x2" into [{qty: 3, text: "Milan Black", match: product}, ...]
  const parseDetailsWithPricing = (detailsText) => {
    if (!detailsText || !produits.length) return { products: [], totalPrice: null, label: "" };

    // Normalize separators: support both "|" and "+"
    const normalizedText = detailsText.replace(/\s*\|\s*/g, " + ");
    const parts = normalizedText.split("+").map(p => p.trim()).filter(Boolean);
    const products = [];
    let totalPrice = 0;
    let allMatched = true;
    const labels = [];

    for (const part of parts) {
      // Extract quantity - supports multiple formats:
      // "3 Milan Black", "Milan Black x3", "Milan Black 3x", "Milan Noir x6"
      let qty = 1;
      let productText = part;

      // Format: "Milan Black x3" or "Milan Black x 3"
      const trailingQtyMatch = part.match(/^(.+?)\s*[xX]\s*(\d+)$/);
      if (trailingQtyMatch) {
        productText = trailingQtyMatch[1].trim();
        qty = parseInt(trailingQtyMatch[2]);
      } else {
        // Format: "3 Milan Black" or "3x Milan Black"
        const leadingQtyMatch = part.match(/^(\d+)\s*[xX]?\s+(.+)$/);
        if (leadingQtyMatch) {
          qty = parseInt(leadingQtyMatch[1]);
          productText = leadingQtyMatch[2].trim();
        }
      }

      const match = matchSingleProduct(productText);
      if (match) {
        const linePrice = qty * match.prix_b2c_ttc;
        totalPrice += linePrice;
        products.push({ qty, text: productText, match, linePrice });
        labels.push(`${qty}x ${match.modele} ${match.couleur}`);
      } else {
        allMatched = false;
        products.push({ qty, text: productText, match: null, linePrice: 0 });
        labels.push(`${qty}x ${productText}`);
      }
    }

    return {
      products,
      totalPrice: allMatched && totalPrice > 0 ? Math.round(totalPrice * 100) / 100 : null,
      label: labels.join(" + "),
      firstMatch: products.find(p => p.match)?.match || null,
    };
  };

  // ---- Raw import ----
  const handleRawTextChange = (text) => {
    setRawText(text);
    const { results: parsed, format, errors } = parseRawText(text);
    setDetectedFormat(format);
    setParseErrors(errors);
    // Auto-match products and calculate prices.
    // GROUPED: all articles of the SAME client stay in ONE delivery (= one receipt).
    const expandedItems = [];
    const FRAIS_LIVRAISON = 25;
    for (const item of parsed) {
      const { products, totalPrice, label, firstMatch } = parseDetailsWithPricing(item.details_commande);
      const matchedProducts = products.filter(p => p.match);
      const totalQty = matchedProducts.reduce((s, p) => s + (p.qty || 1), 0) || item.quantite || 1;
      const articlesPrice = item.prix || totalPrice || 0;
      expandedItems.push({
        ...item,
        quantite: totalQty,
        // Prix du fichier (articles) — les frais de zone sont ajoutés par le backend (sauf si "frais inclus")
        prix: articlesPrice > 0 ? articlesPrice : null,
        frais_livraison: FRAIS_LIVRAISON,
        produit_id: firstMatch?.id || "",
        produit_sku: firstMatch?.sku || "",
        produit_label: label || item.details_commande,
        produits_lies: matchedProducts.map(p => ({
          produit_id: p.match.id, sku: p.match.sku,
          modele: p.match.modele, couleur: p.match.couleur,
          quantite: p.qty, prix_unitaire: p.match.prix_b2c_ttc,
        })),
      });
    }
    setParsedItems(expandedItems);
    setSelectedPreview(new Set());
    if (expandedItems.length > 0) setShowPreview(true);
  };

  const updateParsedItem = (idx, field, value) => {
    setParsedItems((prev) => {
      const copy = [...prev];
      copy[idx] = { ...copy[idx], [field]: value };
      return copy;
    });
  };

  const removeParsedItem = (idx) => {
    setParsedItems((prev) => prev.filter((_, i) => i !== idx));
    setSelectedPreview((prev) => {
      const next = new Set();
      prev.forEach((v) => { if (v < idx) next.add(v); else if (v > idx) next.add(v - 1); });
      return next;
    });
  };

  const removeSelectedPreview = () => {
    setParsedItems((prev) => prev.filter((_, i) => !selectedPreview.has(i)));
    setSelectedPreview(new Set());
  };

  const removeDuplicates = () => {
    setParsedItems((prev) => prev.filter((_, i) => !duplicateFlags[i].inBatch && !duplicateFlags[i].inDb));
    setSelectedPreview(new Set());
    toast.success("Doublons supprimes");
  };

  const togglePreviewSelect = (idx) => {
    setSelectedPreview((prev) => {
      const next = new Set(prev);
      if (next.has(idx)) next.delete(idx);
      else next.add(idx);
      return next;
    });
  };

  const toggleAllPreview = () => {
    if (selectedPreview.size === parsedItems.length) {
      setSelectedPreview(new Set());
    } else {
      setSelectedPreview(new Set(parsedItems.map((_, i) => i)));
    }
  };

  const handleRawImport = async () => {
    if (parsedItems.length === 0) {
      toast.error("Aucune livraison detectee");
      return;
    }
    setImporting(true);
    try {
      const payload = parsedItems.map((item) => {
        // Use produits_lies directly (already built during parsing)
        const produits_lies = (item.produits_lies || []).map(pl => ({
          produit_id: pl.produit_id,
          sku: pl.sku,
          modele: pl.modele,
          couleur: pl.couleur,
          quantite: pl.quantite || parseInt(item.quantite) || 1,
        }));
        return {
          client_nom: item.client_nom,
          client_telephone: item.client_telephone,
          client_email: item.client_email || "",
          adresse: item.adresse,
          ville: item.ville,
          code_postal: item.code_postal,
          pays: item.pays,
          details_commande: item.details_commande,
          quantite: parseInt(item.quantite) || 1,
          prix: item.prix || null,
          frais_livraison: item.frais_livraison ?? 25,
          notes: item.notes || "",
          date_livraison_prevue: importDate || null,
          produits_lies,
        };
      });
      const response = await axios.post(
        `${API}/livraisons/bulk`,
        { 
          livraisons: payload,
          est_historique: importHistorique,
          deduire_stock: importDeduireStock,
          date_livraison_effective: importHistorique ? importDate : null,
          frais_inclus: importFraisInclus,
        },
        { headers }
      );
      const result = response.data;
      if (result.success > 0) toast.success(`${result.success} livraisons importees !`);
      if (result.errors > 0) toast.warning(`${result.errors} erreurs`);

      // Collect imported IDs for auto tournée creation
      const importedIds = result.created_ids || [];
      setRawDialogOpen(false);
      setRawText("");
      setParsedItems([]);
      setShowPreview(false);
      setSelectedPreview(new Set());
      await fetchData();

      // Show post-import dialog if we have IDs
      if (importedIds.length > 0) {
        setPostImportIds(importedIds);
        setShowPostImport(true);
      }
    } catch (error) {
      toast.error("Erreur lors de l'import");
    } finally {
      setImporting(false);
    }
  };

  const handleCreateTourneeFromImport = async () => {
    if (postImportIds.length === 0) return;
    setCreatingTournee(true);
    try {
      const today = importDate || new Date().toISOString().split("T")[0];
      const response = await axios.post(
        `${API}/tournees`,
        {
          nom: `Tournee du ${today.split("-").reverse().join("/")}`,
          date: today,
          livraisons_ids: postImportIds,
        },
        { headers }
      );
      // If historical, mark tournée as terminée
      if (importHistorique) {
        await axios.put(`${API}/tournees/${response.data.id}`, { statut: "terminee" }, { headers });
      }
      toast.success("Tournee creee et optimisee !");
      setShowPostImport(false);
      setPostImportIds([]);
      // Redirect to tournees page
      window.location.href = "/tournees";
    } catch (error) {
      toast.error("Erreur lors de la creation de la tournee");
    } finally {
      setCreatingTournee(false);
    }
  };

  // ---- Main table selection ----
  const toggleRowSelect = (id) => {
    setSelectedRows((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleAllRows = () => {
    if (selectedRows.size === filteredLivraisons.length) {
      setSelectedRows(new Set());
    } else {
      setSelectedRows(new Set(filteredLivraisons.map((l) => l.id)));
    }
  };

  const handleBulkDelete = async () => {
    if (selectedRows.size === 0) return;
    if (!window.confirm(`Déplacer ${selectedRows.size} livraison(s) vers la corbeille ?\n\nVous pourrez les restaurer pendant 15 jours.`)) return;
    try {
      await axios.post(`${API}/livraisons/bulk-delete`, { ids: [...selectedRows] }, { headers });
      toast.success(`${selectedRows.size} livraison(s) déplacée(s) vers la corbeille`);
      setSelectedRows(new Set());
      fetchData();
    } catch (error) {
      toast.error("Erreur lors de la suppression");
    }
  };

  const handleBulkStatusChange = async (status) => {
    if (selectedRows.size === 0) return;
    if (status === "reportee" || status === "annulee") {
      setStatusChangeTarget({ bulk: true, ids: [...selectedRows] });
      setNewStatut(status);
      setStatusNote("");
      setStatusDialogOpen(true);
      return;
    }
    try {
      await Promise.all(
        [...selectedRows].map((id) =>
          axios.put(`${API}/livraisons/${id}`, { statut: status }, { headers })
        )
      );
      toast.success(`${selectedRows.size} livraison(s) mise(s) a jour`);
      setSelectedRows(new Set());
      fetchData();
    } catch (error) {
      toast.error("Erreur");
    }
  };

  const handleBulkMerge = async () => {
    if (selectedRows.size < 2) {
      toast.error("Sélectionne au moins 2 livraisons du même client à fusionner");
      return;
    }
    if (!window.confirm(`Fusionner ${selectedRows.size} livraisons en une seule (même client) ?`)) return;
    try {
      const res = await axios.post(`${API}/livraisons/merge`, { ids: [...selectedRows] }, { headers });
      toast.success(res.data.message);
      setSelectedRows(new Set());
      fetchData();
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Erreur lors de la fusion");
    }
  };

  const handleBulkTransformFacture = async () => {
    if (selectedRows.size === 0) return;
    try {
      const res = await axios.post(`${API}/livraisons/transform-to-facture`, { ids: [...selectedRows] }, { headers });
      toast.success(`${res.data.count} facture(s) créée(s) — montants modifiables dans Devis & Factures`);
      setSelectedRows(new Set());
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Erreur lors de la transformation");
    }
  };

  // ---- Status change with notes ----
  const handleStatutChange = async (livraison, newStatus) => {
    if (newStatus === "reportee" || newStatus === "annulee") {
      setStatusChangeTarget(livraison);
      setNewStatut(newStatus);
      setStatusNote("");
      setStatusDialogOpen(true);
      return;
    }
    try {
      await axios.put(`${API}/livraisons/${livraison.id}`, { statut: newStatus }, { headers });
      toast.success("Statut mis a jour");
      fetchData();
    } catch (error) {
      toast.error("Erreur lors de la mise a jour");
    }
  };

  const confirmStatusChange = async () => {
    if (!statusChangeTarget) return;
    try {
      if (statusChangeTarget.bulk) {
        await Promise.all(
          statusChangeTarget.ids.map((id) => {
            const liv = livraisons.find((l) => l.id === id);
            const notes = statusNote
              ? `${liv?.notes ? liv.notes + " | " : ""}[${newStatut.toUpperCase()}] ${statusNote}`
              : liv?.notes || "";
            return axios.put(`${API}/livraisons/${id}`, { statut: newStatut, notes }, { headers });
          })
        );
        toast.success(`${statusChangeTarget.ids.length} livraison(s) mise(s) a jour`);
        setSelectedRows(new Set());
      } else {
        const update = {
          statut: newStatut,
          notes: statusNote
            ? `${statusChangeTarget.notes ? statusChangeTarget.notes + " | " : ""}[${newStatut.toUpperCase()}] ${statusNote}`
            : statusChangeTarget.notes || "",
        };
        await axios.put(`${API}/livraisons/${statusChangeTarget.id}`, update, { headers });
        toast.success("Statut mis a jour");
      }
      setStatusDialogOpen(false);
      setStatusChangeTarget(null);
      fetchData();
    } catch (error) {
      toast.error("Erreur");
    }
  };

  // ---- Form ----
  const resetForm = () => {
    setFormData({
      client_nom: "",
      client_telephone: "",
      client_email: "",
      adresse: "",
      ville: "",
      code_postal: "",
      pays: "Belgique",
      details_commande: "",
      quantite: 1,
      prix: "",
      frais_livraison: 25,
      notes: "",
      date_livraison_prevue: "",
      est_historique: false,
      deduire_stock: true,
      canal: "B2C",
      parrain_id: "",
      produits_lies: [],
      mode_commande: "livraison",
      client_societe: "",
      client_tva: "",
      remarques: "",
      montant_total: "",
      acompte: "",
      acompte_mode: "liquide",
    });
    setParrainSearch("");
    setEditingLivraison(null);
  };

  const recalcPrixFromItems = (rows) => {
    const sub = rows.reduce((a, p) => a + (parseFloat(p.prix_unitaire) || 0) * (parseInt(p.quantite) || 0), 0);
    const frais = (formData.frais_livraison !== "" && formData.frais_livraison != null) ? parseFloat(formData.frais_livraison) : 25;
    return (sub + (frais || 0)).toFixed(2);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const isDepot = formData.mode_commande === "vente_sur_place";
    if (!formData.client_nom || (!isDepot && (!formData.adresse || !formData.ville))) {
      toast.error("Veuillez remplir les champs obligatoires");
      return;
    }
    // Produits reliés : à la CRÉATION, auto-détection depuis le texte "Détails commande" (ex. « 8x Milan Beige »)
    let effLies = Array.isArray(formData.produits_lies) ? [...formData.produits_lies] : [];
    if (!editingLivraison) {
      const hasSku = effLies.some(p => p.sku);
      if (!hasSku && formData.details_commande) {
        const { products } = parseDetailsWithPricing(formData.details_commande);
        const auto = products.filter(p => p.match).map(p => ({
          produit_id: p.match.id, sku: p.match.sku, modele: p.match.modele,
          couleur: p.match.couleur, quantite: p.qty || 1, prix_unitaire: p.match.prix_b2c_ttc || 0,
        }));
        if (auto.length > 0) effLies = auto;
      }
      if (!effLies.some(p => p.sku)) {
        toast.error("Produit non reconnu. Écris-le comme « 8x Milan Beige », ou ajoute-le via « Ajouter » — sinon le stock ne sera pas déduit.");
        return;
      }
    }
    try {
      // Si des produits sont reliés, la quantité totale et le libellé details_commande sont recalculés automatiquement.
      const hasMix = effLies.length > 0;
      const derivedQte = hasMix
        ? effLies.reduce((a, p) => a + (parseInt(p.quantite) || 0), 0)
        : formData.quantite;
      const derivedDetails = hasMix
        ? effLies
            .map(p => `${p.quantite}x ${p.modele || "Produit"} ${p.couleur || ""}`.trim())
            .join(" + ")
        : formData.details_commande;

      const payload = {
        ...formData,
        prix: formData.prix ? parseFloat(formData.prix) : null,
        frais_livraison: (formData.frais_livraison !== "" && formData.frais_livraison != null) ? parseFloat(formData.frais_livraison) : 25,
        quantite: derivedQte,
        details_commande: derivedDetails,
        produits_lies: hasMix
          ? effLies.map(p => ({
              produit_id: p.produit_id || "",
              sku: p.sku || "",
              modele: p.modele || "",
              couleur: p.couleur || "",
              quantite: parseInt(p.quantite) || 1,
              prix_unitaire: parseFloat(p.prix_unitaire) || 0,
            }))
          : undefined,
        // Pour livraisons historiques
        statut_initial: formData.est_historique ? "livree" : "en_attente",
        deduire_stock: formData.deduire_stock,
        date_livraison_effective: formData.est_historique ? formData.date_livraison_prevue : null,
        parrain_id: formData.parrain_id || null,
      };
      if (editingLivraison) {
        await axios.put(`${API}/livraisons/${editingLivraison.id}`, payload, { headers });
        toast.success("Livraison mise a jour !");
      } else {
        await axios.post(`${API}/livraisons`, payload, { headers });
        if (formData.est_historique) {
          toast.success(formData.deduire_stock 
            ? "Livraison historique créée (stock déduit)" 
            : "Client importé (sans impact stock/stats)");
        } else {
          toast.success("Livraison creee !");
        }
      }
      setDialogOpen(false);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error("Erreur lors de l'enregistrement");
    }
  };

  const handleEdit = (livraison) => {
    setEditingLivraison(livraison);
    setFormData({
      client_nom: livraison.client_nom,
      client_telephone: livraison.client_telephone,
      client_email: livraison.client_email || "",
      adresse: livraison.adresse,
      ville: livraison.ville,
      code_postal: livraison.code_postal,
      pays: livraison.pays,
      details_commande: livraison.details_commande,
      quantite: livraison.quantite,
      prix: livraison.prix || "",
      frais_livraison: livraison.frais_livraison ?? 25,
      notes: livraison.notes || "",
      date_livraison_prevue: livraison.date_livraison_prevue || "",
      est_historique: false,
      deduire_stock: true,
      parrain_id: livraison.parrain_id || "",
      produits_lies: (livraison.produits_lies || []).map(p => ({
        produit_id: p.produit_id || "",
        sku: p.sku || "",
        modele: p.modele || "",
        couleur: p.couleur || "",
        quantite: p.quantite || 1,
        prix_unitaire: p.prix_unitaire ?? 0,
      })),
    });
    if (livraison.parrain_id) {
      const p = parrainsList.find((x) => x.id === livraison.parrain_id);
      setParrainSearch(p ? p.nom : "");
    } else {
      setParrainSearch("");
    }
    setDialogOpen(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Supprimer cette livraison ?")) return;
    try {
      await axios.delete(`${API}/livraisons/${id}`, { headers });
      toast.success("Livraison supprimee");
      setSelectedRows((prev) => { const next = new Set(prev); next.delete(id); return next; });
      fetchData();
    } catch (error) {
      toast.error("Erreur lors de la suppression");
    }
  };

  const openAddTourneeDialog = async (target) => {
    setAddTourneeLivraison(target);
    setSelectedTourneeId("");
    const today = new Date().toISOString().split("T")[0];
    const baseDate = target?.date_livraison_prevue || today;
    setNewTourneeDate(baseDate);
    setNewTourneeNom(`Tournée du ${baseDate.split("-").reverse().join("/")}`);
    setAddTourneeDialogOpen(true);
    try {
      const res = await axios.get(`${API}/tournees`, { headers });
      const actives = (res.data || []).filter(t => t.statut !== "terminee" && t.statut !== "annulee");
      setTourneesActives(actives);
      setTourneeMode(actives.length === 0 ? "nouvelle" : "existante");
    } catch {
      toast.error("Erreur lors du chargement des tournées");
    }
  };

  const openAddTourneeBulk = () => {
    if (selectedRows.size === 0) return;
    const ids = [...selectedRows];
    openAddTourneeDialog({ bulkIds: ids, client_nom: `${ids.length} livraison(s) sélectionnée(s)`, ville: "", date_livraison_prevue: null });
  };

  const handleAddToTournee = async () => {
    if (!addTourneeLivraison) return;
    const ids = addTourneeLivraison.bulkIds || [addTourneeLivraison.id];
    if (ids.length === 0) return;
    setAddingToTournee(true);
    try {
      if (tourneeMode === "nouvelle") {
        if (!newTourneeNom.trim()) { toast.error("Donnez un nom à la tournée"); setAddingToTournee(false); return; }
        const res = await axios.post(`${API}/tournees`, {
          nom: newTourneeNom.trim(),
          date: newTourneeDate || new Date().toISOString().split("T")[0],
          livraisons_ids: ids,
        }, { headers });
        toast.success(`Nouvelle tournée créée avec ${ids.length} client(s) !`);
        setAddTourneeDialogOpen(false);
        setSelectedRows(new Set());
        window.location.href = `/tournees?open=${res.data.id}`;
        return;
      }
      if (!selectedTourneeId) { setAddingToTournee(false); return; }
      const res = await axios.post(`${API}/tournees/${selectedTourneeId}/add-livraisons`,
        { livraisons_ids: ids }, { headers });
      toast.success(`${res.data.added ?? ids.length} livraison(s) ajoutée(s) à la tournée !`);
      setAddTourneeDialogOpen(false);
      setSelectedRows(new Set());
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur lors de l'ajout à la tournée");
    } finally {
      setAddingToTournee(false);
    }
  };

  const filteredLivraisons = livraisons.filter((l) => {
    const matchesSearch =
      l.client_nom.toLowerCase().includes(searchTerm.toLowerCase()) ||
      l.adresse.toLowerCase().includes(searchTerm.toLowerCase()) ||
      l.ville.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatut = filterStatut === "all" || l.statut === filterStatut;
    const matchesVille = filterVille === "all" || (l.ville || "").toLowerCase() === filterVille.toLowerCase();
    const matchesDateStart = !filterDateStart || (l.date_livraison_prevue && l.date_livraison_prevue >= filterDateStart);
    const matchesDateEnd = !filterDateEnd || (l.date_livraison_prevue && l.date_livraison_prevue <= filterDateEnd);
    const matchesTournee =
      filterTournee === "all" ||
      (filterTournee === "avec" && l.tournee_id) ||
      (filterTournee === "sans" && !l.tournee_id);
    return matchesSearch && matchesStatut && matchesVille && matchesDateStart && matchesDateEnd && matchesTournee;
  });

  const villesDisponibles = useMemo(() => {
    const set = new Set();
    livraisons.forEach((l) => {
      if (l.ville && l.ville.trim()) set.add(l.ville.trim());
    });
    return Array.from(set).sort();
  }, [livraisons]);

  const activeFiltersCount = [
    filterVille !== "all",
    !!filterDateStart,
    !!filterDateEnd,
    filterTournee !== "all",
  ].filter(Boolean).length;

  const resetAdvancedFilters = () => {
    setFilterVille("all");
    setFilterDateStart("");
    setFilterDateEnd("");
    setFilterTournee("all");
  };

  // Group by date
  const groupedByDate = useMemo(() => {
    const groups = {};
    filteredLivraisons.forEach((l) => {
      const date = l.date_livraison_prevue || "sans_date";
      if (!groups[date]) groups[date] = [];
      groups[date].push(l);
    });
    // Sort dates: real dates first (ascending), "sans_date" last
    const sortedKeys = Object.keys(groups).sort((a, b) => {
      if (a === "sans_date") return 1;
      if (b === "sans_date") return -1;
      return a.localeCompare(b);
    });
    return sortedKeys.map((key) => ({ date: key, livraisons: groups[key] }));
  }, [filteredLivraisons]);

  const formatDateLabel = (dateStr) => {
    if (dateStr === "sans_date") return "Sans date";
    try {
      const d = new Date(dateStr + "T00:00:00");
      return d.toLocaleDateString("fr-FR", { weekday: "long", day: "numeric", month: "long", year: "numeric" });
    } catch {
      return dateStr;
    }
  };

  return (
    <div className="space-y-6 animate-fade-in" data-testid="livraisons-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-slate-800">Livraisons</h1>
          <p className="text-slate-500 mt-1">Gestion des livraisons clients</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={async () => {
            try {
              const res = await axios.post(`${API}/livraisons/regeocode`, {}, { headers: { Authorization: `Bearer ${token}` } });
              toast.success(res.data.message);
              fetchData();
            } catch { toast.error("Erreur re-géocodage"); }
          }} data-testid="regeocode-btn">
            <MapPin className="w-4 h-4 mr-2" />
            Re-géocoder
          </Button>
          <Dialog open={rawDialogOpen} onOpenChange={(open) => { setRawDialogOpen(open); if (!open) { setRawText(""); setParsedItems([]); setShowPreview(false); setSelectedPreview(new Set()); setDetectedFormat("unknown"); setParseErrors([]); setImportHistorique(false); setImportDeduireStock(false); setImportFraisInclus(false); } }}>
            <DialogTrigger asChild>
              <Button variant="outline" data-testid="raw-import-btn">
                <Upload className="w-4 h-4 mr-2" />
                Import texte brut
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto" data-testid="raw-import-dialog">
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <FileText className="w-5 h-5 text-indigo-600" />
                  Import intelligent - Collez vos donnees
                </DialogTitle>
              </DialogHeader>
              <div className="space-y-4 mt-4">
                <div className="p-3 bg-blue-50 rounded-lg text-sm">
                  <p className="font-medium text-blue-800 mb-2">Collez directement vos donnees — 3 formats supportes :</p>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 text-xs text-blue-700">
                    <div className="bg-white/60 p-2 rounded border border-blue-100">
                      <span className="font-semibold">Tableur (Tab)</span>
                      <p className="opacity-80 mt-0.5">Copie depuis Excel/Sheets</p>
                    </div>
                    <div className="bg-white/60 p-2 rounded border border-blue-100">
                      <span className="font-semibold">Pipe (|)</span>
                      <p className="opacity-80 mt-0.5">Nom | Tel | Adresse | ...</p>
                    </div>
                    <div className="bg-white/60 p-2 rounded border border-blue-100">
                      <span className="font-semibold">Texte libre</span>
                      <p className="opacity-80 mt-0.5">Nom Tel Adresse, CP Ville</p>
                    </div>
                  </div>
                </div>

                <Textarea
                  value={rawText}
                  onChange={(e) => handleRawTextChange(e.target.value)}
                  placeholder={`Collez ici — exemples :\n\n[Tableur] Karima Mouradi\t0488239217\tChaussee de Louvain 160, 1210 Saint-Josse\t3\t130\tMilan Black\n[Pipe]    Karima Mouradi | 0488239217 | Chaussee de Louvain 160, 1210 Saint-Josse | Milan Black\n[Texte]   Karima Mouradi 0488239217 Chaussee de Louvain 160, 1210 Saint-Josse 3 130 Milan Black`}
                  rows={5}
                  className="font-mono text-sm"
                  data-testid="raw-text-input"
                />

                {/* Format detection badge */}
                {rawText.trim() && (
                  <div className="flex items-center gap-3 flex-wrap" data-testid="format-detection">
                    <Badge className={`${parsedItems.length > 0 ? "bg-emerald-100 text-emerald-700 border-emerald-200" : "bg-slate-100 text-slate-500 border-slate-200"} text-xs`}>
                      Format detecte : {FORMAT_LABELS[detectedFormat] || detectedFormat}
                    </Badge>
                    {parseErrors.length > 0 && (
                      <Badge className="bg-amber-100 text-amber-700 border-amber-200 text-xs" data-testid="parse-errors-badge">
                        <AlertTriangle className="w-3 h-3 mr-1" />
                        {parseErrors.length} ligne{parseErrors.length > 1 ? "s" : ""} non reconnue{parseErrors.length > 1 ? "s" : ""}
                      </Badge>
                    )}
                  </div>
                )}

                {parsedItems.length > 0 && (
                  <div className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg">
                    <Calendar className="w-5 h-5 text-indigo-600 flex-shrink-0" />
                    <div className="flex items-center gap-2 flex-1">
                      <span className="text-sm font-medium text-slate-700">
                        {importHistorique ? "Date des livraisons passées :" : "Date de livraison :"}
                      </span>
                      <Input
                        type="date"
                        value={importDate}
                        onChange={(e) => setImportDate(e.target.value)}
                        max={importHistorique ? new Date().toISOString().split("T")[0] : undefined}
                        className="w-44 h-8"
                        data-testid="import-date-input"
                      />
                    </div>
                  </div>
                )}

                {parsedItems.length > 0 && (
                  <div className="space-y-2 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                    <div className="flex items-center gap-3">
                      <Checkbox
                        id="import_historique"
                        checked={importHistorique}
                        onCheckedChange={(v) => {
                          setImportHistorique(!!v);
                          if (!v) setImportDeduireStock(false);
                          // Set date to yesterday if switching to historique
                          if (v && importDate >= new Date().toISOString().split("T")[0]) {
                            setImportDate(new Date(Date.now() - 86400000).toISOString().split("T")[0]);
                          }
                        }}
                        data-testid="import-historique-checkbox"
                      />
                      <div>
                        <Label htmlFor="import_historique" className="font-medium cursor-pointer text-amber-800">
                          Anciennes livraisons (déjà effectuées)
                        </Label>
                        <p className="text-xs text-amber-600 mt-0.5">Ces livraisons seront enregistrées avec le statut "Livrée"</p>
                      </div>
                    </div>
                    {importHistorique && (
                      <div className="ml-7 flex items-center gap-3 mt-2">
                        <Checkbox
                          id="import_deduire_stock"
                          checked={importDeduireStock}
                          onCheckedChange={(v) => setImportDeduireStock(!!v)}
                          data-testid="import-deduire-stock-checkbox"
                        />
                        <div>
                          <Label htmlFor="import_deduire_stock" className="font-medium cursor-pointer text-sm">
                            Déduire du stock et comptabiliser dans les stats
                          </Label>
                          <p className="text-xs text-slate-500 mt-0.5">
                            {importDeduireStock 
                              ? "Le stock sera déduit et les ventes enregistrées" 
                              : "Stock inchangé — décochez si le stock a déjà été renouvelé"}
                          </p>
                        </div>
                      </div>
                    )}
                    <div className="flex items-center gap-3 pt-2 border-t border-amber-200">
                      <Checkbox
                        id="import_frais_inclus"
                        checked={importFraisInclus}
                        onCheckedChange={(v) => setImportFraisInclus(!!v)}
                        data-testid="import-frais-inclus-checkbox"
                      />
                      <div>
                        <Label htmlFor="import_frais_inclus" className="font-medium cursor-pointer text-amber-800">
                          Frais de livraison déjà inclus dans le prix
                        </Label>
                        <p className="text-xs text-amber-600 mt-0.5">
                          {importFraisInclus
                            ? "✅ Le prix du fichier sera gardé tel quel — aucun frais ajouté"
                            : "Décoché : les frais de zone seront ajoutés au prix du fichier"}
                        </p>
                      </div>
                    </div>
                  </div>
                )}

                {parsedItems.length > 0 && (
                  <div className="flex items-center justify-between flex-wrap gap-2">
                    <div className="flex items-center gap-2 text-sm">
                      <Check className="w-4 h-4 text-green-600" />
                      <span className="font-medium text-green-700">
                        {parsedItems.length} livraison{parsedItems.length > 1 ? "s" : ""} detectee{parsedItems.length > 1 ? "s" : ""}
                      </span>
                    </div>
                    <div className="flex items-center gap-2">
                      {duplicateCount > 0 && (
                        <Button
                          size="sm"
                          variant="outline"
                          className="text-orange-600 border-orange-200 hover:bg-orange-50"
                          onClick={removeDuplicates}
                          data-testid="remove-duplicates-btn"
                        >
                          <AlertTriangle className="w-4 h-4 mr-1" />
                          Supprimer {duplicateCount} doublon{duplicateCount > 1 ? "s" : ""}
                        </Button>
                      )}
                      {selectedPreview.size > 0 && (
                        <Button
                          size="sm"
                          variant="outline"
                          className="text-red-600 border-red-200 hover:bg-red-50"
                          onClick={removeSelectedPreview}
                          data-testid="remove-selected-preview-btn"
                        >
                          <Trash2 className="w-4 h-4 mr-1" />
                          Supprimer ({selectedPreview.size})
                        </Button>
                      )}
                    </div>
                  </div>
                )}

                {/* Preview Table */}
                {showPreview && parsedItems.length > 0 && (
                  <div className="border rounded-lg overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow className="bg-slate-50">
                          <TableHead className="w-10">
                            <Checkbox
                              checked={selectedPreview.size === parsedItems.length && parsedItems.length > 0}
                              onCheckedChange={toggleAllPreview}
                              data-testid="select-all-preview"
                            />
                          </TableHead>
                          <TableHead className="w-8">#</TableHead>
                          <TableHead>Nom</TableHead>
                          <TableHead>Telephone</TableHead>
                          <TableHead>Adresse</TableHead>
                          <TableHead>CP</TableHead>
                          <TableHead>Ville</TableHead>
                          <TableHead className="w-16">Qte</TableHead>
                          <TableHead className="w-20">Prix</TableHead>
                          <TableHead>Details</TableHead>
                          <TableHead>Produit (stock)</TableHead>
                          <TableHead className="w-10"></TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {parsedItems.map((item, idx) => {
                          const flag = duplicateFlags[idx];
                          const isDup = flag?.inBatch || flag?.inDb;
                          return (
                            <TableRow
                              key={idx}
                              className={isDup ? "bg-orange-50" : ""}
                              data-testid={`preview-row-${idx}`}
                            >
                              <TableCell>
                                <Checkbox
                                  checked={selectedPreview.has(idx)}
                                  onCheckedChange={() => togglePreviewSelect(idx)}
                                />
                              </TableCell>
                              <TableCell className="text-xs">
                                <div className="flex items-center gap-1">
                                  <span className="text-slate-400">{idx + 1}</span>
                                  {isDup && (
                                    <span title={flag.inDb ? "Existe deja en base" : "Doublon dans le lot"}>
                                      <AlertTriangle className="w-3.5 h-3.5 text-orange-500" />
                                    </span>
                                  )}
                                </div>
                              </TableCell>
                              <TableCell>
                                <Input
                                  value={item.client_nom}
                                  onChange={(e) => updateParsedItem(idx, "client_nom", e.target.value)}
                                  className={`h-8 text-sm min-w-[120px] ${isDup ? "border-orange-300" : ""}`}
                                  data-testid={`preview-nom-${idx}`}
                                />
                              </TableCell>
                              <TableCell>
                                <Input
                                  value={item.client_telephone}
                                  onChange={(e) => updateParsedItem(idx, "client_telephone", e.target.value)}
                                  className="h-8 text-sm min-w-[100px]"
                                />
                                <Input
                                  value={item.client_email || ""}
                                  onChange={(e) => updateParsedItem(idx, "client_email", e.target.value)}
                                  placeholder="email (optionnel)"
                                  className={`h-7 text-xs min-w-[100px] mt-1 ${item.client_email ? "border-emerald-300 bg-emerald-50" : ""}`}
                                  data-testid={`preview-email-${idx}`}
                                />
                              </TableCell>
                              <TableCell>
                                <Input
                                  value={item.adresse}
                                  onChange={(e) => updateParsedItem(idx, "adresse", e.target.value)}
                                  className={`h-8 text-sm min-w-[160px] ${isDup ? "border-orange-300" : ""}`}
                                />
                              </TableCell>
                              <TableCell>
                                <Input
                                  value={item.code_postal}
                                  onChange={(e) => updateParsedItem(idx, "code_postal", e.target.value)}
                                  className="h-8 text-sm w-16"
                                />
                              </TableCell>
                              <TableCell>
                                <Input
                                  value={item.ville}
                                  onChange={(e) => updateParsedItem(idx, "ville", e.target.value)}
                                  className="h-8 text-sm min-w-[100px]"
                                />
                              </TableCell>
                              <TableCell>
                                <Input
                                  type="number"
                                  value={item.quantite}
                                  onChange={(e) => updateParsedItem(idx, "quantite", parseInt(e.target.value) || 1)}
                                  className="h-8 text-sm w-16"
                                />
                              </TableCell>
                              <TableCell>
                                <Input
                                  type="number"
                                  value={item.prix || ""}
                                  onChange={(e) => updateParsedItem(idx, "prix", e.target.value ? parseFloat(e.target.value) : null)}
                                  className={`h-8 text-sm w-20 ${item.prix && item.produit_id ? "border-green-300 bg-green-50 font-medium" : ""}`}
                                  placeholder="--"
                                  title={item.prix && item.produit_id ? "Prix calculé automatiquement" : "Prix manuel"}
                                />
                              </TableCell>
                              <TableCell>
                                <Input
                                  value={item.details_commande}
                                  onChange={(e) => updateParsedItem(idx, "details_commande", e.target.value)}
                                  className="h-8 text-sm min-w-[120px]"
                                />
                              </TableCell>
                              <TableCell>
                                <Select
                                  value={item.produit_id || "none"}
                                  onValueChange={(val) => {
                                    if (val === "none") {
                                      updateParsedItem(idx, "produit_id", "");
                                      updateParsedItem(idx, "produit_sku", "");
                                      updateParsedItem(idx, "produit_label", "");
                                    } else {
                                      const p = produits.find((pr) => pr.id === val);
                                      if (p) {
                                        updateParsedItem(idx, "produit_id", p.id);
                                        updateParsedItem(idx, "produit_sku", p.sku);
                                        updateParsedItem(idx, "produit_label", `${p.modele} ${p.couleur}`);
                                      }
                                    }
                                  }}
                                >
                                  <SelectTrigger className={`h-8 text-xs min-w-[150px] ${item.produit_id ? "border-green-300 bg-green-50" : "border-orange-300 bg-orange-50"}`} data-testid={`product-select-${idx}`}>
                                    <SelectValue placeholder="Choisir..." />
                                  </SelectTrigger>
                                  <SelectContent>
                                    <SelectItem value="none">-- Aucun --</SelectItem>
                                    {produits.map((p) => (
                                      <SelectItem key={p.id} value={p.id}>
                                        {p.sku} — {p.modele} {p.couleur} ({p.stock_restant})
                                      </SelectItem>
                                    ))}
                                  </SelectContent>
                                </Select>
                              </TableCell>
                              <TableCell>
                                <Button size="sm" variant="ghost" onClick={() => removeParsedItem(idx)} className="h-8 w-8 p-0 text-red-500 hover:text-red-700">
                                  <X className="w-4 h-4" />
                                </Button>
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </div>
                )}

                {/* Duplicate warning */}
                {duplicateCount > 0 && (
                  <div className="p-3 bg-orange-50 border border-orange-200 rounded-lg flex items-start gap-2" data-testid="duplicate-warning">
                    <AlertTriangle className="w-5 h-5 text-orange-500 mt-0.5 flex-shrink-0" />
                    <div className="text-sm">
                      <p className="font-medium text-orange-800">
                        {duplicateCount} doublon{duplicateCount > 1 ? "s" : ""} detecte{duplicateCount > 1 ? "s" : ""}
                      </p>
                      <p className="text-orange-600">
                        {duplicateFlags.filter((f) => f.inDb).length > 0 && "Certaines livraisons existent deja en base. "}
                        {duplicateFlags.filter((f) => f.inBatch).length > 0 && "Certaines lignes sont identiques dans le lot. "}
                        Vous pouvez les supprimer ou les importer quand meme.
                      </p>
                    </div>
                  </div>
                )}

                <Button
                  onClick={handleRawImport}
                  disabled={importing || parsedItems.length === 0}
                  className="w-full bg-indigo-600 hover:bg-indigo-700"
                  data-testid="submit-raw-import-btn"
                >
                  {importing ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Import en cours...
                    </>
                  ) : (
                    <>
                      <Upload className="w-4 h-4 mr-2" />
                      {importHistorique 
                        ? `Importer ${parsedItems.length} ancienne${parsedItems.length > 1 ? "s" : ""} livraison${parsedItems.length > 1 ? "s" : ""}`
                        : `Importer ${parsedItems.length} livraison${parsedItems.length > 1 ? "s" : ""}`
                      }
                    </>
                  )}
                </Button>

                {importing && (
                  <p className="text-xs text-center text-slate-500">
                    Le geocodage peut prendre ~1 seconde par adresse (limite API gratuite)
                  </p>
                )}
              </div>
            </DialogContent>
          </Dialog>

          <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetForm(); }}>
            <DialogTrigger asChild>
              <Button className="bg-indigo-600 hover:bg-indigo-700" data-testid="add-livraison-btn">
                <Plus className="w-4 h-4 mr-2" />
                Nouvelle livraison
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="livraison-dialog">
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <Truck className="w-5 h-5 text-indigo-600" />
                  {editingLivraison ? "Modifier la livraison" : "Nouvelle livraison"}
                </DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4 mt-4">
                {/* Recherche client existant (recommande) */}
                {!editingLivraison && (
                  <div className="relative bg-indigo-50/60 border border-indigo-200 rounded-lg p-3 space-y-2" data-testid="client-search-block">
                    <Label className="flex items-center gap-2 text-indigo-700 font-semibold text-sm">
                      <UserCheck className="w-4 h-4" />
                      Client existant (recommande)
                    </Label>
                    <div className="relative">
                      <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                      <Input
                        value={clientSearchQuery}
                        onChange={(e) => setClientSearchQuery(e.target.value)}
                        onFocus={() => clientSearchResults.length > 0 && setClientSearchOpen(true)}
                        placeholder="Rechercher par nom, téléphone, ville…"
                        className="pl-9 bg-white"
                        data-testid="client-search-input"
                      />
                      {clientSearchOpen && clientSearchResults.length > 0 && (
                        <div className="absolute z-50 left-0 right-0 mt-1 bg-white border border-slate-200 rounded-lg shadow-lg max-h-64 overflow-y-auto" data-testid="client-search-results">
                          {clientSearchResults.map((c) => (
                            <button
                              type="button"
                              key={c.id}
                              onClick={() => selectExistingClient(c)}
                              className="w-full text-left px-3 py-2 hover:bg-indigo-50 border-b border-slate-100 last:border-b-0"
                              data-testid={`client-search-result-${c.id}`}
                            >
                              <div className="flex items-center justify-between gap-2">
                                <span className="font-medium text-slate-800 truncate">{c.nom || "—"}</span>
                                {c.nombre_commandes > 0 && (
                                  <span className="text-xs bg-indigo-100 text-indigo-700 px-1.5 py-0.5 rounded-full flex-shrink-0">
                                    {c.nombre_commandes} cmd
                                  </span>
                                )}
                              </div>
                              <div className="text-xs text-slate-500 truncate">
                                {c.telephone || ""} {c.telephone && (c.ville || c.adresse) && "•"} {c.ville || c.adresse || ""}
                              </div>
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                    <p className="text-xs text-slate-500 italic">
                      Tape pour retrouver un ancien client → ses coordonnées se remplissent automatiquement.
                    </p>
                  </div>
                )}

                {/* Mode commande : Livraison / Vente sur place */}
                {!editingLivraison && (
                  <div className="flex gap-2" data-testid="mode-commande-toggle">
                    <button
                      type="button"
                      onClick={() => setFormData({ ...formData, mode_commande: "livraison" })}
                      className={`flex-1 px-3 py-2.5 rounded-md text-sm font-medium transition ${formData.mode_commande === "livraison" ? "bg-indigo-600 text-white shadow-sm" : "bg-slate-100 text-slate-600 hover:bg-slate-200"}`}
                      data-testid="mode-livraison-btn"
                    >
                      🚚 Livraison
                    </button>
                    <button
                      type="button"
                      onClick={() => setFormData({ ...formData, mode_commande: "vente_sur_place", adresse: "", ville: "", code_postal: "", frais_livraison: 0 })}
                      className={`flex-1 px-3 py-2.5 rounded-md text-sm font-medium transition ${formData.mode_commande === "vente_sur_place" ? "bg-indigo-600 text-white shadow-sm" : "bg-slate-100 text-slate-600 hover:bg-slate-200"}`}
                      data-testid="mode-depot-btn"
                    >
                      🏬 Vente sur place
                    </button>
                  </div>
                )}

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                  <div className="space-y-2">
                    <Label>Nom du client *</Label>
                    <Input value={formData.client_nom} onChange={(e) => setFormData({ ...formData, client_nom: e.target.value })} placeholder="Jean Dupont" required data-testid="client-nom-input" />
                  </div>
                  <div className="space-y-2">
                    <Label>Telephone</Label>
                    <Input value={formData.client_telephone} onChange={(e) => setFormData({ ...formData, client_telephone: e.target.value })} placeholder="+32 470 12 34 56" data-testid="client-tel-input" />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Email <span className="text-slate-400 font-normal">(optionnel)</span></Label>
                  <Input type="email" value={formData.client_email} onChange={(e) => setFormData({ ...formData, client_email: e.target.value })} placeholder="client@email.com" data-testid="client-email-input" />
                </div>
                {/* ── LOT 1 : Société / TVA / Remarques ── */}
                <CompanyAutocomplete
                  onSelect={(c) => setFormData((prev) => ({
                    ...prev,
                    client_societe: c.nom || prev.client_societe,
                    client_tva: c.tva || prev.client_tva,
                    adresse: c.adresse || prev.adresse,
                    code_postal: c.code_postal || prev.code_postal,
                    ville: c.ville || prev.ville,
                    pays: c.pays === "FR" ? "France" : "Belgique",
                    canal: "B2B",
                  }))}
                />
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div className="space-y-2">
                    <Label>Société <span className="text-slate-400 font-normal">(optionnel)</span></Label>
                    <Input value={formData.client_societe} onChange={(e) => setFormData({ ...formData, client_societe: e.target.value })} placeholder="ACME SPRL" data-testid="client-societe-input" />
                  </div>
                  <div className="space-y-2">
                    <Label>N° TVA <span className="text-slate-400 font-normal">(optionnel)</span></Label>
                    <Input value={formData.client_tva} onChange={(e) => setFormData({ ...formData, client_tva: e.target.value })} placeholder="BE0123.456.789" data-testid="client-tva-input" />
                  </div>
                </div>
                {formData.mode_commande !== "vente_sur_place" && (
                  <>
                    <div className="space-y-2">
                      <Label>Adresse *</Label>
                      <Input value={formData.adresse} onChange={(e) => setFormData({ ...formData, adresse: e.target.value })} placeholder="Rue de la Gare 15" required={formData.mode_commande !== "vente_sur_place"} data-testid="adresse-input" />
                    </div>
                    <div className="grid grid-cols-3 gap-4">
                      <div className="space-y-2">
                        <Label>Code postal</Label>
                        <Input value={formData.code_postal} onChange={(e) => setFormData({ ...formData, code_postal: e.target.value })} placeholder="1000" data-testid="cp-input" />
                      </div>
                      <div className="space-y-2 col-span-2">
                        <Label>Ville *</Label>
                        <Input value={formData.ville} onChange={(e) => setFormData({ ...formData, ville: e.target.value })} placeholder="Bruxelles" required={formData.mode_commande !== "vente_sur_place"} data-testid="ville-input" />
                      </div>
                    </div>
                  </>
                )}
                {formData.mode_commande === "vente_sur_place" && (
                  <p className="text-xs bg-amber-50 border border-amber-200 rounded-md px-3 py-2 text-amber-800">
                    🏬 Vente sur place — pas d&apos;adresse de livraison ni de frais.
                  </p>
                )}
                <div className="space-y-2">
                  <Label>Details commande</Label>
                  <Textarea value={formData.details_commande} onChange={(e) => setFormData({ ...formData, details_commande: e.target.value })} placeholder="2x Chaise Milan Noir, 1x Table..." rows={2} data-testid="details-input" disabled={formData.produits_lies?.length > 0} />
                  {formData.produits_lies?.length > 0 && (
                    <p className="text-xs text-slate-400 italic">Champ auto-généré depuis les produits ci-dessous</p>
                  )}
                  {!editingLivraison && !(formData.produits_lies?.some(p => p.sku)) && formData.details_commande?.trim() && (() => {
                    const { products } = parseDetailsWithPricing(formData.details_commande);
                    const matched = products.filter(p => p.match);
                    const unmatched = products.filter(p => !p.match);
                    return (
                      <div className="text-xs space-y-0.5" data-testid="auto-detect-preview">
                        {matched.length > 0 && (
                          <p className="text-emerald-600 font-medium">✅ Détecté : {matched.map(p => `${p.qty}x ${p.match.modele} ${p.match.couleur}`).join(" + ")} — stock relié automatiquement</p>
                        )}
                        {unmatched.length > 0 && (
                          <p className="text-orange-600">⚠️ Non reconnu : {unmatched.map(p => p.text).join(", ")} — vérifie l'orthographe (ex. « 8x Milan Beige ») ou ajoute-le manuellement.</p>
                        )}
                      </div>
                    );
                  })()}
                </div>

                {/* Editeur Produits (commandes mixtes) — création + édition (relie le stock) */}
                {(
                  <div className="border border-indigo-200 rounded-lg p-3 bg-indigo-50/40 space-y-2" data-testid="produits-lies-editor">
                    <div className="flex items-center justify-between">
                      <Label className="font-semibold text-indigo-700">Produits de la commande</Label>
                      <Button type="button" size="sm" variant="outline" className="border-indigo-300"
                        onClick={() => setFormData({
                          ...formData,
                          produits_lies: [...(formData.produits_lies || []), { produit_id: "", sku: "", modele: "", couleur: "", quantite: 1 }],
                        })}
                        data-testid="add-produit-lie-btn"
                      >
                        <Plus className="w-3 h-3 mr-1" /> Ajouter
                      </Button>
                    </div>
                    {(!formData.produits_lies || formData.produits_lies.length === 0) ? (
                      <p className="text-xs text-slate-500 italic">Astuce : écris simplement le produit dans « Détails commande » (ex. « 8x Milan Beige ») — il sera relié au stock automatiquement. Ou clique « Ajouter » pour composer une commande mixte.</p>
                    ) : (
                      <div className="space-y-2">
                        {formData.produits_lies.map((p, idx) => (
                          <div key={idx} className="grid grid-cols-12 gap-2 items-center" data-testid={`produit-lie-row-${idx}`}>
                            <div className="col-span-5">
                              <Select
                                value={p.produit_id || "none"}
                                onValueChange={(val) => {
                                  const rows = [...formData.produits_lies];
                                  if (val === "none") {
                                    rows[idx] = { ...rows[idx], produit_id: "", sku: "", modele: "", couleur: "" };
                                  } else {
                                    const prod = produits.find(x => x.id === val);
                                    if (prod) {
                                      const pu = (!rows[idx].prix_unitaire || rows[idx].prix_unitaire <= 0) ? (prod.prix_b2c_ttc || 0) : rows[idx].prix_unitaire;
                                      rows[idx] = { ...rows[idx], produit_id: prod.id, sku: prod.sku, modele: prod.modele, couleur: prod.couleur, prix_unitaire: pu };
                                    }
                                  }
                                  setFormData({ ...formData, produits_lies: rows, prix: recalcPrixFromItems(rows) });
                                }}
                              >
                                <SelectTrigger data-testid={`produit-lie-select-${idx}`}><SelectValue placeholder="Choisir un modèle…" /></SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="none">-- Aucun (produit libre) --</SelectItem>
                                  {produits.map((pr) => (
                                    <SelectItem key={pr.id} value={pr.id}>{pr.sku} — {pr.modele} {pr.couleur} (stock {pr.stock_restant})</SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            </div>
                            <div className="col-span-2">
                              <Input
                                type="number" min="1"
                                value={p.quantite}
                                onChange={(e) => {
                                  const rows = [...formData.produits_lies];
                                  rows[idx] = { ...rows[idx], quantite: parseInt(e.target.value) || 1 };
                                  setFormData({ ...formData, produits_lies: rows, prix: recalcPrixFromItems(rows) });
                                }}
                                data-testid={`produit-lie-qte-${idx}`}
                                title="Quantité"
                              />
                            </div>
                            <div className="col-span-4">
                              <Input
                                type="number" step="0.01" min="0"
                                value={p.prix_unitaire ?? ""}
                                placeholder="Prix unit. €"
                                onChange={(e) => {
                                  const rows = [...formData.produits_lies];
                                  rows[idx] = { ...rows[idx], prix_unitaire: e.target.value === "" ? "" : parseFloat(e.target.value) };
                                  setFormData({ ...formData, produits_lies: rows, prix: recalcPrixFromItems(rows) });
                                }}
                                data-testid={`produit-lie-prix-${idx}`}
                                title="Prix unitaire €"
                              />
                            </div>
                            <div className="col-span-1">
                              <Button type="button" size="sm" variant="ghost" className="text-red-500 h-9 w-9 p-0"
                                onClick={() => {
                                  const rows = formData.produits_lies.filter((_, i) => i !== idx);
                                  setFormData({ ...formData, produits_lies: rows, prix: recalcPrixFromItems(rows) });
                                }}
                                data-testid={`produit-lie-remove-${idx}`}
                              ><X className="w-4 h-4" /></Button>
                            </div>
                          </div>
                        ))}
                        <p className="text-xs text-indigo-700 pt-1">
                          Total : <b>{formData.produits_lies.reduce((a, p) => a + (parseInt(p.quantite) || 0), 0)}</b> chaise(s)
                          {" — "}
                          {formData.produits_lies.map(p => `${p.quantite}x ${p.modele || "?"} ${p.couleur || ""}`.trim()).join(" + ")}
                          {" — "}
                          Sous-total articles : <b>{formData.produits_lies.reduce((a, p) => a + (parseFloat(p.prix_unitaire) || 0) * (parseInt(p.quantite) || 0), 0).toFixed(2)} €</b>
                        </p>
                      </div>
                    )}
                  </div>
                )}

                <div className="grid grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <Label>Quantite</Label>
                    <Input type="number" min="1" value={formData.quantite} onChange={(e) => setFormData({ ...formData, quantite: parseInt(e.target.value) })} data-testid="quantite-input" disabled={editingLivraison && formData.produits_lies?.length > 0} />
                  </div>
                  <div className="space-y-2">
                    <Label>Prix (EUR)</Label>
                    <Input type="number" step="0.01" value={formData.prix} onChange={(e) => setFormData({ ...formData, prix: e.target.value })} placeholder="0.00" data-testid="prix-input" />
                  </div>
                  <div className="space-y-2">
                    <Label>Frais livraison (EUR)</Label>
                    <Input type="number" step="0.01" value={formData.frais_livraison} onChange={(e) => setFormData({ ...formData, frais_livraison: e.target.value })} placeholder="25.00" data-testid="frais-livraison-input" />
                  </div>
                  <div className="space-y-2">
                    <Label>Date prevue</Label>
                    <Input type="date" value={formData.date_livraison_prevue} onChange={(e) => setFormData({ ...formData, date_livraison_prevue: e.target.value })} data-testid="date-input" />
                  </div>
                  <div className="space-y-2 col-span-3 sm:col-span-1">
                    <Label>Type de client</Label>
                    <div className="flex gap-2" data-testid="canal-toggle">
                      <button
                        type="button"
                        onClick={() => setFormData({ ...formData, canal: "B2C" })}
                        className={`flex-1 px-3 py-2 rounded-md text-sm font-medium transition ${formData.canal === "B2C" ? "bg-indigo-600 text-white shadow-sm" : "bg-slate-100 text-slate-600 hover:bg-slate-200"}`}
                        data-testid="canal-b2c-btn"
                      >
                        B2C (Particulier)
                      </button>
                      <button
                        type="button"
                        onClick={() => setFormData({ ...formData, canal: "B2B" })}
                        className={`flex-1 px-3 py-2 rounded-md text-sm font-medium transition ${formData.canal === "B2B" ? "bg-indigo-600 text-white shadow-sm" : "bg-slate-100 text-slate-600 hover:bg-slate-200"}`}
                        data-testid="canal-b2b-btn"
                      >
                        B2B (Pro)
                      </button>
                    </div>
                  </div>
                </div>

                {/* ── LOT 2 : Acompte à la création ── */}
                {!editingLivraison && (
                  <div className="border border-emerald-200 bg-emerald-50/40 rounded-lg p-3 space-y-2" data-testid="acompte-block">
                    <Label className="text-emerald-700 font-semibold text-sm">💰 Acompte reçu (optionnel)</Label>
                    <div className="grid grid-cols-3 gap-2">
                      <div>
                        <Label className="text-xs text-slate-500">Montant</Label>
                        <Input type="number" step="0.01" min="0" value={formData.acompte} onChange={(e) => setFormData({ ...formData, acompte: e.target.value })} placeholder="0.00" data-testid="acompte-input" />
                      </div>
                      <div className="col-span-2">
                        <Label className="text-xs text-slate-500">Mode paiement</Label>
                        <select
                          value={formData.acompte_mode}
                          onChange={(e) => setFormData({ ...formData, acompte_mode: e.target.value })}
                          className="w-full h-10 px-3 rounded-md border border-slate-200 bg-white text-sm"
                          data-testid="acompte-mode-select"
                        >
                          <option value="liquide">Cash</option>
                          <option value="qr">QR code</option>
                          <option value="terminal">Terminal</option>
                          <option value="virement">Virement</option>
                        </select>
                      </div>
                    </div>
                    {formData.acompte && parseFloat(formData.acompte) > 0 && parseFloat(formData.prix || 0) > 0 && (
                      <p className="text-xs text-emerald-700 font-medium">
                        Reste à payer : <strong>{(parseFloat(formData.prix || 0) - parseFloat(formData.acompte || 0)).toFixed(2)} €</strong>
                      </p>
                    )}
                  </div>
                )}

                <div className="space-y-2">
                  <Label>Remarques <span className="text-slate-400 font-normal">(interne)</span></Label>
                  <Input value={formData.remarques} onChange={(e) => setFormData({ ...formData, remarques: e.target.value })} placeholder="Note interne sur le client…" data-testid="remarques-input" />
                </div>
                <div className="space-y-2">
                  <Label>Notes</Label>
                  <Textarea value={formData.notes} onChange={(e) => setFormData({ ...formData, notes: e.target.value })} placeholder="Instructions speciales..." rows={2} data-testid="notes-input" />
                </div>

                {/* Parrainé par */}
                <div className="space-y-2">
                  <Label className="flex items-center gap-1.5">
                    <Gift className="w-4 h-4 text-purple-600" />
                    Parrainé par (optionnel)
                  </Label>
                  <div className="space-y-1.5">
                    <Input
                      placeholder="Rechercher un parrain par nom ou téléphone…"
                      value={parrainSearch}
                      onChange={(e) => setParrainSearch(e.target.value)}
                      data-testid="parrain-search-input"
                    />
                    {parrainSearch.trim().length >= 2 && (
                      <div className="max-h-32 overflow-y-auto border border-slate-200 rounded-lg divide-y divide-slate-100">
                        {parrainsList
                          .filter((p) => {
                            const q = parrainSearch.toLowerCase();
                            return (p.nom || "").toLowerCase().includes(q) || (p.telephone || "").includes(q);
                          })
                          .slice(0, 8)
                          .map((p) => (
                            <button
                              key={p.id}
                              type="button"
                              onClick={() => { setFormData({ ...formData, parrain_id: p.id }); setParrainSearch(p.nom); }}
                              className="w-full text-left px-3 py-1.5 hover:bg-indigo-50 text-xs flex justify-between"
                              data-testid={`parrain-option-${p.id}`}
                            >
                              <span className="font-medium">{p.nom}</span>
                              <span className="text-slate-400">{p.telephone}</span>
                            </button>
                          ))}
                      </div>
                    )}
                    {formData.parrain_id && (
                      <div className="inline-flex items-center gap-1 px-2 py-1 bg-purple-100 text-purple-700 rounded-full text-xs">
                        Parrain sélectionné
                        <button type="button" onClick={() => { setFormData({ ...formData, parrain_id: "" }); setParrainSearch(""); }} className="hover:text-purple-900">×</button>
                      </div>
                    )}
                  </div>
                </div>
                
                {/* Options historique - seulement en création */}
                {!editingLivraison && (
                  <div className="bg-slate-50 border rounded-lg p-4 space-y-4">
                    <div className="flex items-center space-x-3">
                      <Checkbox
                        id="est_historique"
                        checked={formData.est_historique}
                        onCheckedChange={(checked) => setFormData({ 
                          ...formData, 
                          est_historique: checked,
                          deduire_stock: checked ? formData.deduire_stock : true 
                        })}
                      />
                      <Label htmlFor="est_historique" className="text-sm font-medium cursor-pointer">
                        Livraison déjà effectuée (historique)
                      </Label>
                    </div>
                    
                    {formData.est_historique && (
                      <div className="ml-6 space-y-3 border-l-2 border-indigo-200 pl-4">
                        <p className="text-xs text-slate-500">
                          Cette livraison sera enregistrée avec le statut "Livrée"
                        </p>
                        
                        <div className="flex items-start space-x-3">
                          <Checkbox
                            id="deduire_stock"
                            checked={formData.deduire_stock}
                            onCheckedChange={(checked) => setFormData({ ...formData, deduire_stock: checked })}
                          />
                          <div>
                            <Label htmlFor="deduire_stock" className="text-sm font-medium cursor-pointer">
                              Déduire du stock actuel
                            </Label>
                            <p className="text-xs text-slate-500 mt-0.5">
                              {formData.deduire_stock 
                                ? "✅ Le stock sera déduit et la vente comptabilisée dans les stats"
                                : "❌ Import client uniquement (pas d'impact sur stock/stats)"
                              }
                            </p>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                )}
                
                <Button type="submit" className="w-full bg-indigo-600 hover:bg-indigo-700" data-testid="submit-livraison-btn">
                  <Truck className="w-4 h-4 mr-2" />
                  {editingLivraison ? "Mettre a jour" : formData.est_historique ? (formData.deduire_stock ? "Enregistrer (avec stock)" : "Importer le client") : "Creer la livraison"}
                </Button>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          <Card><CardContent className="p-4"><p className="text-sm text-slate-500">Total</p><p className="text-2xl font-bold text-slate-800">{stats.total_livraisons}</p></CardContent></Card>
          <Card className="bg-yellow-50 border-yellow-200"><CardContent className="p-4"><p className="text-sm text-yellow-600">En attente</p><p className="text-2xl font-bold text-yellow-700">{stats.en_attente}</p></CardContent></Card>
          <Card className="bg-blue-50 border-blue-200"><CardContent className="p-4"><p className="text-sm text-blue-600">En cours</p><p className="text-2xl font-bold text-blue-700">{stats.en_cours}</p></CardContent></Card>
          <Card className="bg-green-50 border-green-200"><CardContent className="p-4"><p className="text-sm text-green-600">Livrees</p><p className="text-2xl font-bold text-green-700">{stats.livrees}</p></CardContent></Card>
          <Card className="bg-red-50 border-red-200"><CardContent className="p-4"><p className="text-sm text-red-600">Annulees</p><p className="text-2xl font-bold text-red-700">{stats.annulees}</p></CardContent></Card>
        </div>
      )}

      {/* Filters + Bulk Actions */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
              <Input placeholder="Rechercher par nom, adresse, ville..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className="pl-10" data-testid="search-livraisons" />
            </div>
            <Select value={filterStatut} onValueChange={setFilterStatut}>
              <SelectTrigger className="w-full sm:w-48" data-testid="filter-statut">
                <Filter className="w-4 h-4 mr-2" />
                <SelectValue placeholder="Filtrer par statut" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tous les statuts</SelectItem>
                {STATUT_OPTIONS.map((opt) => (
                  <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button
              type="button"
              variant="outline"
              onClick={() => setShowAdvancedFilters((v) => !v)}
              className="sm:w-auto relative"
              data-testid="toggle-advanced-filters-btn"
            >
              <Filter className="w-4 h-4 mr-2" />
              Filtres avancés
              {activeFiltersCount > 0 && (
                <span className="ml-2 px-1.5 py-0.5 text-xs font-bold rounded-full bg-indigo-600 text-white">
                  {activeFiltersCount}
                </span>
              )}
            </Button>
          </div>

          {showAdvancedFilters && (
            <div className="mt-4 p-4 bg-slate-50 border border-slate-200 rounded-lg space-y-3" data-testid="advanced-filters-panel">
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
                <div>
                  <label className="text-xs font-medium text-slate-600 block mb-1">Ville</label>
                  <Select value={filterVille} onValueChange={setFilterVille}>
                    <SelectTrigger data-testid="filter-ville">
                      <SelectValue placeholder="Toutes les villes" />
                    </SelectTrigger>
                    <SelectContent className="max-h-60">
                      <SelectItem value="all">Toutes les villes</SelectItem>
                      {villesDisponibles.map((v) => (
                        <SelectItem key={v} value={v}>{v}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="text-xs font-medium text-slate-600 block mb-1">Date début (prévue)</label>
                  <Input
                    type="date"
                    value={filterDateStart}
                    onChange={(e) => setFilterDateStart(e.target.value)}
                    data-testid="filter-date-start"
                  />
                </div>
                <div>
                  <label className="text-xs font-medium text-slate-600 block mb-1">Date fin (prévue)</label>
                  <Input
                    type="date"
                    value={filterDateEnd}
                    onChange={(e) => setFilterDateEnd(e.target.value)}
                    data-testid="filter-date-end"
                  />
                </div>
                <div>
                  <label className="text-xs font-medium text-slate-600 block mb-1">Tournée</label>
                  <Select value={filterTournee} onValueChange={setFilterTournee}>
                    <SelectTrigger data-testid="filter-tournee">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Toutes</SelectItem>
                      <SelectItem value="avec">Avec tournée</SelectItem>
                      <SelectItem value="sans">Sans tournée</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              {activeFiltersCount > 0 && (
                <div className="flex justify-end">
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={resetAdvancedFilters}
                    className="text-slate-500 hover:text-slate-700"
                    data-testid="reset-advanced-filters-btn"
                  >
                    Réinitialiser les filtres
                  </Button>
                </div>
              )}
            </div>
          )}

          {/* Bulk actions bar */}
          {selectedRows.size > 0 && (
            <div className="mt-4 flex items-center gap-3 p-3 bg-indigo-50 rounded-lg" data-testid="bulk-actions-bar">
              <CheckSquare className="w-5 h-5 text-indigo-600" />
              <span className="text-sm font-medium text-indigo-700">
                {selectedRows.size} selectionnee{selectedRows.size > 1 ? "s" : ""}
              </span>
              <div className="flex gap-2 ml-auto flex-wrap">
                <Select onValueChange={handleBulkStatusChange}>
                  <SelectTrigger className="h-8 w-40 text-xs" data-testid="bulk-status-select">
                    <SelectValue placeholder="Changer statut" />
                  </SelectTrigger>
                  <SelectContent>
                    {STATUT_OPTIONS.map((opt) => (
                      <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Button size="sm" variant="outline" className="text-violet-700 border-violet-200 hover:bg-violet-50 h-8" onClick={handleBulkMerge} data-testid="bulk-merge-btn">
                  <Package className="w-4 h-4 mr-1" />
                  Fusionner
                </Button>
                <Button size="sm" variant="outline" className="text-emerald-700 border-emerald-200 hover:bg-emerald-50 h-8" onClick={openAddTourneeBulk} data-testid="bulk-add-tournee-btn">
                  <Route className="w-4 h-4 mr-1" />
                  Ajouter à une tournée
                </Button>
                <Button size="sm" variant="outline" className="text-emerald-700 border-emerald-200 hover:bg-emerald-50 h-8" onClick={handleBulkTransformFacture} data-testid="bulk-transform-facture-btn">
                  <FileText className="w-4 h-4 mr-1" />
                  Transformer en facture
                </Button>
                <Button size="sm" variant="outline" className="text-red-600 border-red-200 hover:bg-red-50 h-8" onClick={handleBulkDelete} data-testid="bulk-delete-btn">
                  <Trash2 className="w-4 h-4 mr-1" />
                  Supprimer
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Table */}
      <Card data-testid="livraisons-table">
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <Truck className="w-5 h-5 text-indigo-600" />
            Liste des livraisons ({filteredLivraisons.length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
            </div>
          ) : filteredLivraisons.length === 0 ? (
            <div className="text-center py-12 text-slate-500">
              <Truck className="w-12 h-12 mx-auto mb-4 opacity-50" />
              <p>Aucune livraison trouvee</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-10">
                      <Checkbox
                        checked={selectedRows.size === filteredLivraisons.length && filteredLivraisons.length > 0}
                        onCheckedChange={toggleAllRows}
                        data-testid="select-all-rows"
                      />
                    </TableHead>
                    <TableHead>Client</TableHead>
                    <TableHead>Adresse</TableHead>
                    <TableHead>Commande</TableHead>
                    <TableHead>Prevu</TableHead>
                    <TableHead>Encaisse</TableHead>
                    <TableHead>Statut</TableHead>
                    <TableHead>Notes</TableHead>
                    <TableHead></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {groupedByDate.map((group) => (
                    <React.Fragment key={`group-${group.date}`}>
                      <TableRow className="bg-slate-50 hover:bg-slate-50">
                        <TableCell colSpan={10} className="py-2">
                          <div className="flex items-center gap-2">
                            <Calendar className="w-4 h-4 text-indigo-500" />
                            <span className="font-semibold text-slate-700 capitalize">{formatDateLabel(group.date)}</span>
                            <Badge variant="outline" className="ml-2 text-xs">{group.livraisons.length}</Badge>
                          </div>
                        </TableCell>
                      </TableRow>
                      {group.livraisons.map((livraison) => (
                        <TableRow key={livraison.id} className={selectedRows.has(livraison.id) ? "bg-indigo-50" : ""} data-testid={`livraison-row-${livraison.id}`}>
                      <TableCell>
                        <Checkbox
                          checked={selectedRows.has(livraison.id)}
                          onCheckedChange={() => toggleRowSelect(livraison.id)}
                          data-testid={`select-row-${livraison.id}`}
                        />
                      </TableCell>
                      <TableCell>
                        <div>
                          <div className="flex items-center gap-2">
                            <p className="font-medium">{livraison.client_nom}</p>
                            {livraison.numero_commande && (
                              <span className="text-xs font-mono bg-indigo-50 text-indigo-600 px-1.5 py-0.5 rounded" data-testid={`numero-commande-${livraison.id}`}>
                                #{livraison.numero_commande}
                              </span>
                            )}
                          </div>
                          {livraison.client_telephone && (
                            <p className="text-sm text-slate-500 flex items-center gap-1">
                              <Phone className="w-3 h-3" />
                              {livraison.client_telephone}
                            </p>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-start gap-1">
                          <MapPin className="w-4 h-4 text-slate-400 mt-0.5 flex-shrink-0" />
                          <div>
                            <p className="text-sm">{livraison.adresse}</p>
                            <p className="text-sm text-slate-500">{livraison.code_postal} {livraison.ville}</p>
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1">
                          <Package className="w-4 h-4 text-slate-400" />
                          <span className="font-medium">{livraison.quantite}x</span>
                        </div>
                        {livraison.details_commande && (
                          <p className="text-xs text-slate-500 mt-1 max-w-32 truncate">{livraison.details_commande}</p>
                        )}
                      </TableCell>
                      <TableCell>
                        {livraison.prix ? (
                          <span className="font-medium text-slate-700">{livraison.prix}EUR</span>
                        ) : <span className="text-slate-400">--</span>}
                      </TableCell>
                      <TableCell>
                        {livraison.statut_paiement === "paye" ? (
                          <div className="flex flex-col gap-0.5">
                            <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-emerald-100 text-emerald-700 font-semibold whitespace-nowrap flex items-center gap-1" title="Payé en ligne via Stripe">
                              💳 Payé en ligne
                            </span>
                            {livraison.paye_par_b2b > 0 && (
                              <span className="text-[10px] text-emerald-600 font-medium">{livraison.paye_par_b2b.toFixed(2)}€</span>
                            )}
                          </div>
                        ) : livraison.montant_encaisse != null ? (
                          <div>
                            <span className={`font-medium ${livraison.montant_encaisse < (livraison.prix || 0) ? "text-orange-600" : "text-green-700"}`}>
                              {livraison.montant_encaisse}EUR
                            </span>
                            {livraison.prix && livraison.montant_encaisse !== livraison.prix && (
                              <p className="text-xs text-orange-500">{(livraison.montant_encaisse - livraison.prix).toFixed(0)}EUR</p>
                            )}
                          </div>
                        ) : livraison.a_encaisser > 0 ? (
                          <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-700 font-semibold whitespace-nowrap" title="À encaisser à la livraison">
                            💰 {livraison.a_encaisser.toFixed(2)}€
                          </span>
                        ) : <span className="text-slate-300">--</span>}
                      </TableCell>
                      <TableCell>
                        <Select value={livraison.statut} onValueChange={(value) => handleStatutChange(livraison, value)}>
                          <SelectTrigger className="w-32 h-8 text-xs" data-testid={`statut-select-${livraison.id}`}>
                            {getStatutBadge(livraison.statut)}
                          </SelectTrigger>
                          <SelectContent>
                            {STATUT_OPTIONS.map((opt) => (
                              <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </TableCell>
                      <TableCell>
                        {(livraison.notes || livraison.note_encaissement) ? (
                          <p className="text-xs text-slate-600 max-w-40 truncate" title={`${livraison.notes || ""} ${livraison.note_encaissement || ""}`}>
                            {livraison.note_encaissement || livraison.notes}
                          </p>
                        ) : <span className="text-slate-300">--</span>}
                      </TableCell>
                      <TableCell>
                        <div className="flex gap-1">
                          {!livraison.tournee_id && livraison.statut !== "livree" && livraison.statut !== "annulee" && (
                            <Button size="sm" variant="ghost" onClick={() => openAddTourneeDialog(livraison)} className="text-emerald-600 hover:text-emerald-700" title="Ajouter à une tournée" data-testid={`add-to-tournee-${livraison.id}`}>
                              <Route className="w-4 h-4" />
                            </Button>
                          )}
                          <Button size="sm" variant="ghost" onClick={() => handleEdit(livraison)} className="text-indigo-600 hover:text-indigo-700" data-testid={`edit-livraison-${livraison.id}`}>
                            <Edit className="w-4 h-4" />
                          </Button>
                          <Button size="sm" variant="ghost" onClick={() => handleDelete(livraison.id)} className="text-red-600 hover:text-red-700" data-testid={`delete-livraison-${livraison.id}`}>
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                      ))}
                    </React.Fragment>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Status Change with Notes Dialog */}
      <Dialog open={statusDialogOpen} onOpenChange={setStatusDialogOpen}>
        <DialogContent className="max-w-md" data-testid="status-note-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertCircle className="w-5 h-5 text-orange-500" />
              {newStatut === "reportee" ? "Reporter la livraison" : "Annuler la livraison"}
              {statusChangeTarget?.bulk && ` (${statusChangeTarget.ids.length})`}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            {statusChangeTarget && !statusChangeTarget.bulk && (
              <div className="p-3 bg-slate-50 rounded-lg">
                <p className="font-medium">{statusChangeTarget.client_nom}</p>
                <p className="text-sm text-slate-500">{statusChangeTarget.adresse}, {statusChangeTarget.code_postal} {statusChangeTarget.ville}</p>
              </div>
            )}
            {statusChangeTarget?.bulk && (
              <div className="p-3 bg-slate-50 rounded-lg">
                <p className="font-medium">{statusChangeTarget.ids.length} livraison(s) selectionnee(s)</p>
              </div>
            )}
            <div className="space-y-2">
              <Label>Raison / Note *</Label>
              <Textarea
                value={statusNote}
                onChange={(e) => setStatusNote(e.target.value)}
                placeholder={newStatut === "reportee" ? "Ex: Client absent, reportee au 25/02..." : "Ex: Adresse incorrecte, commande annulee par le client..."}
                rows={3}
                data-testid="status-note-input"
              />
            </div>
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setStatusDialogOpen(false)}>Annuler</Button>
            <Button
              onClick={confirmStatusChange}
              className={newStatut === "reportee" ? "bg-orange-600 hover:bg-orange-700" : "bg-red-600 hover:bg-red-700"}
              data-testid="confirm-status-btn"
            >
              {newStatut === "reportee" ? "Reporter" : "Confirmer l'annulation"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Post-Import: Create Tournée Dialog */}
      <Dialog open={showPostImport} onOpenChange={setShowPostImport}>
        <DialogContent className="max-w-md" data-testid="post-import-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Check className="w-6 h-6 text-green-600" />
              {postImportIds.length} livraisons importees !
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            <p className="text-slate-600">Que voulez-vous faire ?</p>
            <Button
              size="lg"
              className="w-full h-14 text-base bg-indigo-600 hover:bg-indigo-700"
              onClick={handleCreateTourneeFromImport}
              disabled={creatingTournee}
              data-testid="create-tournee-from-import-btn"
            >
              {creatingTournee ? (
                <><Loader2 className="w-5 h-5 mr-2 animate-spin" />Creation et optimisation...</>
              ) : (
                <><Route className="w-5 h-5 mr-2" />Creer une tournee avec ces livraisons</>
              )}
            </Button>
            <Button
              variant="outline"
              className="w-full"
              onClick={() => { setShowPostImport(false); setPostImportIds([]); }}
              data-testid="close-post-import-btn"
            >
              Fermer — je creerai la tournee plus tard
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Ajout à une tournée existante */}
      <Dialog open={addTourneeDialogOpen} onOpenChange={setAddTourneeDialogOpen}>
        <DialogContent className="max-w-md" data-testid="add-tournee-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Route className="w-5 h-5 text-emerald-600" />
              Ajouter à une tournée
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            <p className="text-sm text-slate-600">
              Livraison : <span className="font-medium">{addTourneeLivraison?.client_nom}</span> — {addTourneeLivraison?.ville}
            </p>

            {/* Choix du mode */}
            <div className="grid grid-cols-2 gap-2">
              <Button type="button" variant={tourneeMode === "existante" ? "default" : "outline"}
                className={tourneeMode === "existante" ? "bg-emerald-600 hover:bg-emerald-700" : ""}
                onClick={() => setTourneeMode("existante")} data-testid="mode-tournee-existante">
                Tournée existante
              </Button>
              <Button type="button" variant={tourneeMode === "nouvelle" ? "default" : "outline"}
                className={tourneeMode === "nouvelle" ? "bg-emerald-600 hover:bg-emerald-700" : ""}
                onClick={() => setTourneeMode("nouvelle")} data-testid="mode-tournee-nouvelle">
                Nouvelle tournée
              </Button>
            </div>

            {tourneeMode === "existante" ? (
              tourneesActives.length === 0 ? (
                <p className="text-sm text-slate-500 text-center py-4">Aucune tournée active. Créez-en une nouvelle.</p>
              ) : (
                <Select value={selectedTourneeId} onValueChange={setSelectedTourneeId}>
                  <SelectTrigger data-testid="select-tournee-target">
                    <SelectValue placeholder="Choisir une tournée..." />
                  </SelectTrigger>
                  <SelectContent>
                    {tourneesActives.map((t) => (
                      <SelectItem key={t.id} value={t.id}>
                        {t.nom} ({(t.livraisons_ids || []).length} arrêts — {t.statut})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )
            ) : (
              <div className="space-y-3">
                <div>
                  <label className="text-xs font-medium text-slate-600 block mb-1">Nom de la tournée</label>
                  <Input value={newTourneeNom} onChange={(e) => setNewTourneeNom(e.target.value)} placeholder="Ex : Tournée Liège" data-testid="new-tournee-nom" />
                </div>
                <div>
                  <label className="text-xs font-medium text-slate-600 block mb-1">Date</label>
                  <Input type="date" value={newTourneeDate} onChange={(e) => setNewTourneeDate(e.target.value)} data-testid="new-tournee-date" />
                </div>
              </div>
            )}

            <Button
              className="w-full bg-emerald-600 hover:bg-emerald-700"
              disabled={addingToTournee || (tourneeMode === "existante" && !selectedTourneeId) || (tourneeMode === "nouvelle" && !newTourneeNom.trim())}
              onClick={handleAddToTournee}
              data-testid="confirm-add-to-tournee-btn"
            >
              {addingToTournee ? "..." : tourneeMode === "nouvelle" ? "Créer la tournée avec ce client" : "Ajouter à cette tournée"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Livraisons;
